import React, { useState, useEffect, useRef } from 'react';
import { X, Loader2, Save, Plus, Trash2, Receipt, ChevronUp, ChevronDown } from 'lucide-react';
import { toast } from 'react-toastify';
import { useSisConfig } from '../../contexts/SisConfigUi';
import { logComponentError } from '../../utils/errFrontend';
import SqlexecMensaje from './SqlexecMensaje';
import { secureFetch } from '../../utils/authInterceptor';
import { formatNumberDesktop } from '../../utils/calculations';
import { evaluarCondicion, conservarCursor, compensarEscalaVh } from '../../utils/formHelpers';
import { resolvePlaceholders } from '../../utils/envioEmails';
import useBankInput from '../../hooks/useBankInput';
import useMoneyPosInput from '../../hooks/useMoneyPosInput';
import SqlexecMasterDetail from './SqlexecMasterDetail';
import SqlexecConfirmDialog from './SqlexecConfirmDialog';
import SqlexecIdTag from '../common/SqlexecIdTag';
import SearchModalField from '../common/SearchModalField';

// ==========================================
// SqlexecMasterDetailLight
// ==========================================
// Componente HERMANO de SqlexecMasterDetail, NO reemplazo.
// Diseñado para casos de "encabezado + detalle simple" SIN:
//   - búsqueda de productos
//   - motor de impuestos (impuestos_extra)
//   - navegación avanzada con Enter entre campos
//   - partida_completa / edición inline tipo VFP
// Soporta en el detail-grid: text, number, select (con dataSource)
// El submit llama a una función PostgreSQL (config.funcion) que recibe
// { data: { master, details } } — exactamente el mismo contrato que ya
// usan fn_sqlexec_master_detail_remisiones, fn_sqlexec_master_detail_cobranza, etc.
// Reglas de negocio cobranza:
//   - EFECTIVO siempre al final: "+ Agregar" se apaga si ya hay línea efectivo o saldo cubierto
//   - Comisión: si captura el máximo exacto → sobre saldoDisponible; si parcial → sobre numFinal
//   - Cambio solo aplica al efectivo: MAX(efectivo - saldoTrasNoEfectivo, 0)
//   - Hint solo aparece si hay saldo pendiente, no hay efectivo, no hay campo enfocado y hay línea pendiente
//   - Guardar habilitado solo si saldoPendienteRender = 0 (el foco en una
//     celda ya NO bloquea Guardar -- blur nativo del navegador corre antes
//     que el click del botón, y onUpdate ya mantiene el valor en vivo)
// ==========================================

const createEmptyDetailRow = (detailConfig, rowNumber) => {
  const emptyRow = {
    _id: `new_${Date.now()}_${rowNumber}`,
    numero: rowNumber
  };
  detailConfig.columns?.forEach(col => {
    if (col.autoIncrement) return;
    emptyRow[col.name] = col.defaultValue !== undefined
      ? col.defaultValue
      : (col.type === 'number' ? 0 : '');
  });
  return emptyRow;
};

// Fecha local (respeta el timezone del navegador), formato YYYY-MM-DD.
// toISOString() SIEMPRE devuelve UTC -- usarlo para "hoy" hace que la
// fecha salte al dia siguiente en cuanto la hora UTC ya cruzo medianoche,
// aunque localmente (Mexico, UTC-6) siga siendo el dia anterior. Se arma
// con los getters locales del Date en vez de toISOString().
const fechaLocalHoy = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Resuelve un sentinela de f.min/f.max a una fecha ISO comparable.
// 'TODAY'/'today' -> fechaLocalHoy() (ya existia). 'PARENT:<campo>' -> valor
// de parentRow[<campo>] -- convertido de DD/MM/YYYY a ISO si viene en ese
// formato (asi lo devuelven las grillas via TO_CHAR(...,'DD/MM/YYYY'), ej.
// parentRow.fecha en Sys_Ventas_Cobranza). Generico: no asume que exista
// 'campo', cualquier component_form puede usar su propio PARENT:<campo>.
// Resuelve el destino de Enter (detail.navigation[col].onEnter). Acepta un
// string fijo (comportamiento actual) o un objeto condicional {field, equals,
// then, else} -- mismo vocabulario que hidden_when/editable_when -- para que
// el destino varie segun el valor de la propia fila (ej. idsatforpago: salta
// a 'importe' si es efectivo, a 'ctabancaria' si no).
const resolverDestinoEnter = (onEnter, row) => {
  if (typeof onEnter === 'string') return onEnter;
  if (onEnter && typeof onEnter === 'object') {
    const cumple = row?.[onEnter.field] === onEnter.equals;
    return cumple ? onEnter.then : onEnter.else;
  }
  return undefined;
};

const resolverFechaLimite = (valor, parentRow) => {
  if (valor === 'TODAY' || valor === 'today') return fechaLocalHoy();
  if (typeof valor === 'string' && valor.startsWith('PARENT:')) {
    const bruto = parentRow?.[valor.slice('PARENT:'.length)];
    if (typeof bruto === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(bruto)) {
      const [d, m, y] = bruto.split('/');
      return `${y}-${m}-${d}`;
    }
    return bruto;
  }
  return valor;
};

// ==========================================
// CAMPO AUTOCOMPLETE — búsqueda inline, sin modal.
// Subcomponente local (mismo patrón que UniversalTooltip/ContextMenu en
// SqlexecTable.jsx) — necesario por reglas de hooks, no es un archivo nuevo.
// Reutiliza /api/sqlexec_generico/query-params (ya existe, ya acepta
// params arbitrarios) — cero endpoints nuevos.
// ==========================================
const AutocompleteHeaderField = ({
  field, value, onChange, onMultiFieldChange, disabled, error, touched,
  inputStyles, labelStyles, errorStyles
}) => {
  const { label, placeholder, required, searchConfig = {}, lockOnSelect } = field;
  const { query, labelField = 'label', valueField = 'value', minChars = 2, debounceMs = 300 } = searchConfig;

  const [texto, setTexto] = useState(field.initialDisplayValue || '');
  const [resultados, setResultados] = useState([]);
  const [abierto, setAbierto] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [focused, setFocused] = useState(false);
  const debounceRef = useRef(null);
  const contenedorRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (contenedorRef.current && !contenedorRef.current.contains(e.target)) setAbierto(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const buscar = (texto) => {
    clearTimeout(debounceRef.current);
    if (texto.length < minChars) { setResultados([]); setAbierto(false); return; }
    debounceRef.current = setTimeout(async () => {
      setCargando(true);
      try {
        const token = sessionStorage.getItem('mapasac_access_token');
        const r = await secureFetch('/api/sqlexec_generico/query-params', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ query, params: [texto] })
        });
        const j = await r.json();
        setResultados(j?.success ? (j.data || []) : []);
        setAbierto(true);
      } catch (e) {
        console.error('Error autocomplete:', e);
        setResultados([]);
      } finally {
        setCargando(false);
      }
    }, debounceMs);
  };

  const seleccionar = (row) => {
    const labelValue = (field?.format === 'uppercase' && typeof row[labelField] === 'string')
      ? row[labelField].toUpperCase()
      : row[labelField];
    setTexto(labelValue);
    setAbierto(false);
    setResultados([]);
    onChange(row[valueField]);
    onMultiFieldChange?.({ [`${field.name}_display`]: labelValue });
  };

  const limpiar = () => { setTexto(''); onChange(null); setResultados([]); };
  const showError = touched && error;
  // Bloqueo tras seleccionar: zero riesgo de cambiar de cliente a medio
  // proceso con documentos/importes ya capturados. Solo se reactiva
  // limpiando la seleccion con el boton 'X' (no oculto por este bloqueo,
  // solo por disabled real del formulario).
  const bloqueadoPorSeleccion = lockOnSelect && !!value && !disabled;
  const inputDisabled = disabled || bloqueadoPorSeleccion;

  return (
    <div ref={contenedorRef} style={{ position: 'relative' }}>
      <label style={{
        fontSize: labelStyles.fontSize || '0.875rem', fontWeight: labelStyles.fontWeight || '500',
        color: labelStyles.textColor || '#374151', display: 'block', marginBottom: labelStyles.marginBottom || '0.5rem'
      }}>
        {label}{required && <span style={{ color: '#ef4444' }}> *</span>}
      </label>
      <input
        type="text"
        value={texto}
        placeholder={placeholder}
        disabled={inputDisabled}
        onChange={(e) => {
          const val = conservarCursor(e.target, (texto) => (field?.format === 'uppercase') ? texto.toUpperCase() : texto);
          setTexto(val); buscar(val); if (value) onChange(null);
        }}
        onFocus={() => { setFocused(true); if (resultados.length > 0) setAbierto(true); }}
        onBlur={() => setFocused(false)}
        style={{
          width: '100%', height: inputStyles.height || '32px', fontSize: inputStyles.fontSize || '0.75rem',
          fontFamily: inputStyles.fontFamily || 'inherit', borderRadius: inputStyles.borderRadius || '6px',
          borderWidth: inputStyles.borderWidth || '1px', borderStyle: 'solid',
          borderColor: showError ? '#ef4444' : focused ? (inputStyles.focusBorderColor || '#3b82f6') : (inputStyles.borderColor || '#d1d5db'),
          boxShadow: focused ? `0 0 0 ${inputStyles.focusRingWidth || '2px'} ${inputStyles.focusBorderColor || '#3b82f6'}40` : 'none',
          padding: `0 ${inputStyles.padding?.x || '0.75rem'}`,
          backgroundColor: inputDisabled ? (inputStyles.disabledBackgroundColor || '#f3f4f6') : (inputStyles.backgroundColor || '#ffffff'),
          color: inputStyles.textColor || '#111827', outline: 'none'
        }}
      />
      {value && !disabled && (
        <button type="button" onClick={limpiar} style={{ position: 'absolute', right: '0.5rem', top: '2.1rem', background: 'none', border: 'none', cursor: 'pointer' }}>
          <X size={14} className="text-gray-400" />
        </button>
      )}
      {abierto && (cargando || resultados.length > 0) && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, marginTop: '2px',
          backgroundColor: '#ffffff', border: `1px solid ${inputStyles.borderColor || '#d1d5db'}`,
          borderRadius: inputStyles.borderRadius || '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          maxHeight: '220px', overflowY: 'auto'
        }}>
          {cargando && <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.75rem', color: '#9ca3af' }}>Buscando...</div>}
          {!cargando && resultados.length === 0 && (
            <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.75rem', color: '#9ca3af' }}>Sin resultados</div>
          )}
          {!cargando && resultados.map((row) => (
            <div
              key={row[valueField]}
              onClick={() => seleccionar(row)}
              style={{ padding: '0.5rem 0.75rem', fontSize: '0.75rem', cursor: 'pointer' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f3f4f6'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              {row[labelField]}
            </div>
          ))}
        </div>
      )}
      {showError && (
        <p style={{ fontSize: errorStyles.fontSize || '0.75rem', color: errorStyles.textColor || '#dc2626', marginTop: errorStyles.marginTop || '0.25rem' }}>
          {error}
        </p>
      )}
    </div>
  );
};

// Componente inline para la celda numérica bancaria
const BankInputCell = ({ value, onUpdate, onCommit, inputStyles, rowsConfig, focusedCell, cellKey, setFocusedCell, dataRow, dataField, onEnter, onPagoExacto }) => {
  const { formatEdit, handleFocus, handleKeyDown, handleBlur } = useBankInput(value, onUpdate, onCommit);
  return (
    <input
      type="text"
      data-row={dataRow}
      data-field={dataField}
      value={focusedCell === cellKey ? formatEdit(value) : formatNumberDesktop(value || 0, 2)}
      onFocus={e => { setFocusedCell(cellKey); handleFocus(e); }}
      onKeyDown={e => {
        if (e.key === 'PageDown') { e.preventDefault(); onPagoExacto?.(); return; }
        if (e.key === 'Enter') { e.preventDefault(); onEnter?.(); return; }
        handleKeyDown(e);
      }}
      onBlur={e => { setFocusedCell(null); handleBlur(e); }}
      onChange={() => {}}
      style={{
        width:           '100%',
        textAlign:       focusedCell === cellKey ? 'left' : 'right',
        fontSize:        inputStyles.fontSize || '0.75rem',
        fontFamily:      inputStyles.fontFamily || 'inherit',
        borderRadius:    inputStyles.borderRadius || '4px',
        borderWidth:     inputStyles.borderWidth || '1px',
        borderStyle:     'solid',
        borderColor:     focusedCell === cellKey ? (inputStyles.focusBorderColor || '#3b82f6') : 'transparent',
        padding:         '0.25rem',
        backgroundColor: focusedCell === cellKey ? (inputStyles.backgroundColor || '#ffffff') : 'transparent',
        outline:         'none',
        caretColor:      focusedCell === cellKey ? (inputStyles.caretColor || '#1e40af') : 'transparent'
      }}
    />
  );
};


// Componente inline para la celda numerica tipo calculadora bancaria / terminal POS
// (digitos entran por la derecha, sin cursor posicionable -- ver useMoneyPosInput)
const MoneyPosInputCell = ({ value, onUpdate, onCommit, inputStyles, focusedCell, cellKey, setFocusedCell, dataRow, dataField, onEnter, onPagoExacto }) => {
  const { handleFocus, handleKeyDown, handleBlur, handleSelect } = useMoneyPosInput(value, onUpdate, onCommit);
  return (
    <input
      type="text"
      inputMode="numeric"
      data-row={dataRow}
      data-field={dataField}
      value={focusedCell === cellKey ? (parseFloat(value) || 0).toFixed(2) : formatNumberDesktop(value || 0, 2)}
      onFocus={e => { setFocusedCell(cellKey); handleFocus(e); }}
      onSelect={handleSelect}
      onKeyDown={e => {
        if (e.key === 'PageDown') { e.preventDefault(); onPagoExacto?.(); return; }
        if (e.key === 'Enter') { e.preventDefault(); onEnter?.(); return; }
        handleKeyDown(e);
      }}
      onBlur={e => { setFocusedCell(null); handleBlur(e); }}
      onChange={() => {}}
      style={{
        width:           '100%',
        textAlign:       'right',
        fontSize:        inputStyles.fontSize || '0.75rem',
        fontFamily:      inputStyles.fontFamily || 'inherit',
        borderRadius:    inputStyles.borderRadius || '4px',
        borderWidth:     inputStyles.borderWidth || '1px',
        borderStyle:     'solid',
        borderColor:     focusedCell === cellKey ? (inputStyles.focusBorderColor || '#3b82f6') : 'transparent',
        padding:         '0.25rem',
        backgroundColor: focusedCell === cellKey ? (inputStyles.backgroundColor || '#ffffff') : 'transparent',
        outline:         'none',
        caretColor:      focusedCell === cellKey ? (inputStyles.caretColor || '#1e40af') : 'transparent'
      }}
    />
  );
};

// Componente inline para captura money-pos en un campo del ENCABEZADO
// (mismo hook/mecanismo que MoneyPosInputCell -- solo cambia el estilo
// visual, que aquí debe verse como campo de formulario, no celda)
const MoneyPosHeaderInput = ({ value, onUpdate, onCommit, inputStyles, focusedCell, cellKey, setFocusedCell, showError, disabled }) => {
  const { handleFocus, handleKeyDown, handleBlur, handleSelect } = useMoneyPosInput(value, onUpdate, onCommit);
  const focused = focusedCell === cellKey;
  return (
    <input
      type="text"
      inputMode="numeric"
      disabled={disabled}
      value={focused ? (parseFloat(value) || 0).toFixed(2) : formatNumberDesktop(value || 0, 2)}
      onFocus={e => { setFocusedCell(cellKey); handleFocus(e); }}
      onSelect={handleSelect}
      onKeyDown={e => {
        if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); return; }
        handleKeyDown(e);
      }}
      onBlur={e => { setFocusedCell(null); handleBlur(e); }}
      onChange={() => {}}
      style={{
        width:           '100%',
        textAlign:       'right',
        height:          inputStyles.height || '32px',
        fontSize:        inputStyles.fontSize || '0.75rem',
        fontFamily:      inputStyles.fontFamily || 'inherit',
        borderRadius:    inputStyles.borderRadius || '6px',
        borderWidth:     inputStyles.borderWidth || '1px',
        borderStyle:     'solid',
        borderColor:     showError ? '#ef4444' : focused ? (inputStyles.focusBorderColor || '#3b82f6') : (inputStyles.borderColor || '#d1d5db'),
        boxShadow:       focused ? `0 0 0 ${inputStyles.focusRingWidth || '2px'} ${inputStyles.focusBorderColor || '#3b82f6'}40` : 'none',
        padding:         `0 ${inputStyles.padding?.x || '0.75rem'}`,
        backgroundColor: disabled ? (inputStyles.disabledBackgroundColor || '#f3f4f6') : (inputStyles.backgroundColor || '#ffffff'),
        color:           inputStyles.textColor || '#111827',
        outline:         'none'
      }}
    />
  );
};

const SqlexecMasterDetailLight = ({
  componentForm,
  mode = 'create',
  initialData = {},
  parentRow,
  onClose,
  onSuccess,
  onRefreshGrid,
  useGenerico = true,
  idreporte = null,
  origenComponentForm = null,
  origenKey = null,
  autorizado = false,
  accionTag = null,
}) => {
  const { tabla_ui, formulario_ui, botones_ui, modales_ui, panel_ui } = useSisConfig();

  // Estilos desde JSONB
  const headerConfig  = tabla_ui?.header        || {};
  const rowsConfig    = tabla_ui?.rows          || {};
  const columnsConfig = tabla_ui?.columns       || {};
  const inputStyles   = formulario_ui?.inputs   || {};
  const selectStyles  = formulario_ui?.selects  || {};
  const labelStyles   = formulario_ui?.labels   || {};
  const errorStyles   = formulario_ui?.errorMessages || {};
  const btnBase       = botones_ui              || {};
  const btnPrimary    = btnBase.primary         || {};
  const btnSecondary  = btnBase.secondary       || {};

  const [schema, setSchema] = useState(null);
  const [idsqlexec, setIdsqlexec] = useState(null); // identificador SQLEXEC real (fila de config)
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [idField, setIdField] = useState(null);

  const [detailSchema, setDetailSchema] = useState(null);
  const [detailData, setDetailData] = useState([]);
  const [selectOptions, setSelectOptions] = useState({});
  const [detailSelectOptions, setDetailSelectOptions] = useState({});

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [focusedField, setFocusedField] = useState(null); // encabezado
  const [focusedCell, setFocusedCell] = useState(null);   // grid detalle
  const [headerCollapsed, setHeaderCollapsed] = useState(false); // acordeón del encabezado (schema.config.collapsibleHeader)

  // Al colapsar el header, baja el scroll para mostrar de inmediato la
  // leyenda de comparación (Capturado/Recibido/Diferencia) -- corre
  // DESPUES de que React ya aplicó el nuevo layout (useEffect), por eso
  // no hace falta rAF/setTimeout. Al expandir no hace nada (ya era correcto).
  useEffect(() => {
    if (!headerCollapsed) return;
    const el = document.querySelector('[data-comparacion-hint]');
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [headerCollapsed]);

  const [showMensaje, setShowMensaje] = useState(false);
  const [mensajeData, setMensajeData] = useState({ type: 'error', title: '', message: '', steps: [], onDismiss: null, technicalInfo: null });

  const loadedRef = useRef(false);
  const isCreate   = mode === 'create';
  const isUpdate   = mode === 'update';
  const isReadOnly = mode === 'view';

  const showMensajeModal = (type, title, message, steps = [], onDismiss = null, technicalInfo = null) => {
    setMensajeData({ type, title, message, steps, onDismiss, technicalInfo });
    setShowMensaje(true);
  };

  const [showConfirma, setShowConfirma] = useState(false);
  const [limpiezas, setLimpiezas] = useState(0); // schema.config.botonLimpiar -- remonta campos con estado propio

  // Valores iniciales del encabezado y del detalle: los usa la carga
  // inicial y el boton Limpiar (schema.config.botonLimpiar).
  const armarFormDataInicial = (s) => {
    const base = {};
    s.fields?.forEach(f => {
      if ((isUpdate || isReadOnly) && initialData.hasOwnProperty(f.name)) {
        base[f.name] = initialData[f.name];
      } else if ((f.disabled || f.type === 'hidden') && parentRow?.[f.name] !== undefined) {
        base[f.name] = parentRow[f.name];
      } else if (f.calculated && parentRow?.[f.name] !== undefined) {
        // null cuenta como vacio (''), no como el texto "null": asi un select
        // requerido sin valor (ej. uso del CFDI) deja Guardar deshabilitado.
        base[f.name] = f.type === 'select'
          ? (parentRow[f.name] === null ? '' : String(parentRow[f.name]))
          : parentRow[f.name];
      } else if (isCreate && initialData?.hasOwnProperty(f.name)) {
        base[f.name] = initialData[f.name];
      } else if (f.defaultValue === 'TODAY') {
        base[f.name] = fechaLocalHoy();
      } else {
        base[f.name] = f.defaultValue ?? '';
      }
    });
    return base;
  };

  const armarDetalleInicial = (detail) => {
    const minRows = detail.minRows || 0;
    return (isCreate && minRows > 0)
      ? Array.from({ length: minRows }, (_, i) => createEmptyDetailRow(detail, i + 1))
      : [];
  };

  // ==========================================
  // CARGA INICIAL
  // ==========================================
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;

    (async () => {
      try {
        const token = sessionStorage.getItem('mapasac_access_token');
        let r, j;

        if (useGenerico) {
          r = await secureFetch(`/api/sqlexec_generico/schema/${componentForm}?idreporte=${idreporte || ''}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } else {
          r = await secureFetch(`/api/sqlexec/${componentForm}/read?_t=${Date.now()}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
        j = await r.json();

        if (!j?.success || !j?.data?.schema) {
          showMensajeModal('error', 'Schema no configurado',
            `No se encontró schema válido para "${componentForm}".`,
            [`Verifica mapasoft.sqlexec para component_form = '${componentForm}'`]);
          setLoading(false);
          return;
        }

        const s = j.data.schema;
        setSchema(s);
        setIdsqlexec(j?.data?.idsqlexec ?? null);

        const primaryField = s.fields?.find(f => f.primary === true);
        setIdField(primaryField?.name || null);

        setFormData(armarFormDataInicial(s));

        if (s.detail) {
          setDetailSchema(s.detail);
          setDetailData(armarDetalleInicial(s.detail));

          const detailSelects = s.detail.columns?.filter(c => c.type === 'select' && c.dataSource?.query) || [];
          for (const col of detailSelects) {
            try {
              const rOpt = await secureFetch('/api/sqlexec/query', {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ query: col.dataSource.query })
              });
              const jOpt = await rOpt.json();
              if (jOpt?.success) setDetailSelectOptions(prev => ({ ...prev, [col.name]: jOpt.data || [] }));
            } catch (e) {
              console.error(`Error cargando opciones detalle ${col.name}:`, e);
            }
          }
        }

        const headerSelects = s.fields?.filter(f => f.type === 'select' && f.dataSource?.query) || [];
        for (const f of headerSelects) {
          try {
            const rOpt = await secureFetch('/api/sqlexec/query', {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({ query: f.dataSource.query })
            });
            const jOpt = await rOpt.json();
            if (jOpt?.success) setSelectOptions(prev => ({ ...prev, [f.name]: jOpt.data || [] }));
          } catch (e) {
            console.error(`Error cargando opciones ${f.name}:`, e);
          }
        }

        setLoading(false);

        // ─── PREFLIGHT GENERICO (schema.preflight) ───
        // Bloqueo de apertura completa del modal, evaluado ANTES de que el
        // usuario vea el formulario. Genérico: cualquier component_form puede
        // declarar schema.preflight en su JSONB sin tocar este código. Reusa
        // /api/sqlexec/query (mismo endpoint de los selects de arriba), que ya
        // resuelve :session_idsucursal server-side (resolverPlaceholders).
        // Fail-open ante error tecnico de red/servidor: las validaciones por
        // documento (loadDetailOnChange) y el respaldo en la funcion SQL siguen
        // protegiendo el caso de negocio real.
        if (isCreate && s?.preflight?.query) {
          try {
            const rPre = await secureFetch('/api/sqlexec/query', {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({ query: s.preflight.query })
            });
            const jPre = await rPre.json();
            const ok = jPre?.success ? !!jPre.data?.[0]?.[s.preflight.campo_ok || 'ok'] : true;
            if (!ok) {
              showMensajeModal('error', s.preflight.title, s.preflight.message,
                s.preflight.steps || [], () => { onClose?.(); }, s.preflight.technicalInfo || null);
              return;
            }
          } catch (e) {
            console.error('Error en preflight:', e);
            logComponentError('SqlexecMasterDetailLight', 'preflight', `${componentForm} - ${e.message}`);
          }
        }

        // ─── ORDEN DE VALIDACIONES AL ABRIR EL MODAL ───
        // Aplica a Generar Factura Y a Cancelación (ambos consumen un timbre
        // de mapasoft.servicios / clave 'TIMBRES').
        //
        // 1º BLOQUEO administrativo (tenant sin config / sin saldo). Si bloquea,
        //    se muestra el modal y se corta (return): no tiene sentido mostrar
        //    los avisos informativos de abajo si la operación ni siquiera va a
        //    poder ejecutarse. saldo_timbres viene de la grilla padre
        //    (Cxc_Diario / Cxc_Diario_Facturas):
        //      -1 = el tenant no tiene NINGUNA fila para clave='TIMBRES'
        //       0 = tiene paquete(s) pero sin saldo disponible
        //    Mismos 2 mensajes que el preflight de guardado (backend/utils/servicios.js).
        const stRaw = s?.mostrar_saldo_timbres ? parentRow?.saldo_timbres : undefined;
        const st = (stRaw === undefined || stRaw === null) ? null : parseInt(stRaw);
        if (isCreate && st === -1) {
          showMensajeModal('error', 'Servicio de timbrado no configurado',
            'El servicio de timbrado no está configurado para tu empresa. Contacte al administrador para configurar.',
            [
              "Verifica que exista una fila en mapasoft.servicios para el tenant_id de esta empresa con clave='TIMBRES'",
              'Si no existe, un administrador debe crearla con saldo y utilizado'
            ],
            () => { onClose?.(); },
            { component_form: componentForm, tabla: 'mapasoft.servicios' });
          return;
        }
        if (isCreate && st === 0) {
          showMensajeModal('error', 'Sin saldo de timbres disponibles',
            'No es posible completar la operación: no hay saldo de timbres disponible para tu empresa. Contacta al administrador para recargar.',
            [], () => { onClose?.(); });
          return;
        }

        // 2º AVISO informativo: esta remisión ya tiene una reserva 'P' en curso
        //    (cliefact.estatus) -- no es bloqueo, solo avisa antes de reintentar.
        //    Solo Generar Factura -- Cxc_Diario trae estatus_cliefact.
        if (isCreate && parentRow?.estatus_cliefact === 'P') {
          showMensajeModal('info', 'Reserva en curso',
            `Esta remisión ya tiene una reserva de factura pendiente (folio #${parentRow.nofactura}). ` +
            'Puedes reintentar el timbrado; el Contribuyente queda fijo desde la reserva original.');
        }

        // 3º AVISO informativo: factura timbrada hace menos de 1 hora -- el PAC
        //    (Finkok) puede tardar en reconocerla en su índice de cancelación;
        //    no es bloqueo (evidencia real 2026-08-11: éxito a los ~14 min tras
        //    un rechazo 205 "Folio Fiscal No Existente" a los ~3 min de timbrado).
        //    Solo Cancelación -- Cxc_Diario_Facturas trae timbrado_reciente.
        if (isCreate && parentRow?.timbrado_reciente === true) {
          showMensajeModal('warning', 'Aviso',
            "Esta factura se timbró hace menos de 1 hora. El PAC (Finkok) puede tardar unos minutos en reconocerla — si la cancelación falla con 'Folio Fiscal No Existente', espera unos minutos y vuelve a intentar.",
            [], () => {});
        }
      } catch (e) {
        console.error('Error cargando schema:', e);
        logComponentError('SqlexecMasterDetailLight', 'load_schema', `${componentForm} - ${e.message}`);
        toast.error('Error de conexión');
        setLoading(false);
      }
    })();
  }, [componentForm, initialData, isCreate, isUpdate, isReadOnly, parentRow, useGenerico, idreporte]);

  // ==========================================
  // CARGA DE DETALLE AL CAMBIAR CAMPO DEL HEADER (loadDetailOnChange)
  // Capacidad genérica: cualquier campo del header puede declarar
  // loadDetailOnChange = { query, params? } -- al cambiar su valor a algo
  // no vacío, se hace fetch (mismo endpoint /api/sqlexec_generico/
  // query-params que ya usan los selects dependientes) y las filas
  // devueltas REEMPLAZAN detailData completo. Solo aplica en creación.
  // ==========================================
  const lastLoadDetailValueRef = useRef({});
  useEffect(() => {
    if (!isCreate || !schema?.fields || !detailSchema) return;

    const campo = schema.fields.find(f => f.loadDetailOnChange);
    if (!campo) return;

    const valorActual = formData[campo.name];
    if (valorActual === lastLoadDetailValueRef.current[campo.name]) return;
    lastLoadDetailValueRef.current[campo.name] = valorActual;

    if (!valorActual) {
      setDetailData([]);
      const resetFields = campo.loadDetailOnChange?.resetFieldsOnClear;
      if (resetFields) {
        setFormData(prev => {
          const next = { ...prev };
          Object.entries(resetFields).forEach(([name, val]) => {
            next[name] = (val === 'TODAY' || val === 'today') ? fechaLocalHoy() : val;
          });
          return next;
        });
      }
      return;
    }

    (async () => {
      const { query, params: paramNames = [campo.name] } = campo.loadDetailOnChange;
      try {
        const token = sessionStorage.getItem('mapasac_access_token');
        const params = paramNames.map(p => formData[p] ?? valorActual);
        const r = await secureFetch('/api/sqlexec_generico/query-params', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ query, params })
        });
        const j = await r.json();
        const filas = j?.success ? (j.data || []) : [];

        const documentoSinCuenta = filas.find(f => f.sin_cuenta_bancaria === true);
        if (documentoSinCuenta) {
          setDetailData([]);
          showMensajeModal(
            'error',
            'Contribuyente sin cuenta bancaria configurada',
            `El contribuyente "${documentoSinCuenta.causante_razonsocial}" (emisor de la factura ${documentoSinCuenta.serie}${documentoSinCuenta.nofactura}) no tiene ninguna cuenta bancaria capturada. No es posible continuar con el cobro mientras exista al menos un documento de este cliente emitido por un contribuyente sin cuenta bancaria.`,
            [
              `Verifica que exista al menos una cuenta en mapasoft.satcausantes.ctabancaria para idsatcausantes=${documentoSinCuenta.idsatcausantes}`,
              'Si no existe, un administrador debe capturarla en Administración → Catálogo de Sucursales → Contribuyentes → Cuentas Bancarias'
            ],
            null,
            { component_form: componentForm, tabla: 'mapasoft.satcausantes', campo: 'ctabancaria', idsatcausantes: documentoSinCuenta.idsatcausantes }
          );
          return;
        }

        setDetailData(filas.map((fila, i) => ({
          ...createEmptyDetailRow(detailSchema, i + 1),
          ...fila
        })));

        // Cuenta bancaria -- campo unico en el header (no por documento):
        // por regla de negocio, un mismo cobro nunca mezcla documentos de
        // causantes distintos, asi que todas las filas ya traen las mismas
        // opciones_cuenta -- basta tomar la de la primera.
        if (filas.length > 0 && filas[0].opciones_cuenta) {
          setSelectOptions(prev => ({ ...prev, ctabancaria: filas[0].opciones_cuenta }));
          if (filas[0].opciones_cuenta.length === 1) {
            setFormData(prev => ({ ...prev, ctabancaria: filas[0].opciones_cuenta[0].value }));
          }
        }
      } catch (e) {
        console.error('Error cargando detalle por loadDetailOnChange:', e);
        setDetailData([]);
      }
    })();
  }, [formData, schema, detailSchema, isCreate]);

  // ==========================================
  // BOTON LIMPIAR (schema.config.botonLimpiar.enabled)
  // Regresa encabezado y partidas a como quedan al abrir, sin cerrar el
  // modal: mismo armado inicial, loadDetailOnChange vuelve a correr como
  // en la apertura, se quitan las opciones de selects que no vienen de
  // dataSource (llenadas en tiempo de captura, ej. ctabancaria) y se
  // remontan los campos con estado propio (autocomplete).
  // ==========================================
  const limpiarFormulario = () => {
    if (!schema) return;
    lastLoadDetailValueRef.current = {};
    setFormData(armarFormDataInicial(schema));
    if (detailSchema) setDetailData(armarDetalleInicial(detailSchema));
    setSelectOptions(prev => Object.fromEntries(Object.entries(prev).filter(([name]) =>
      schema.fields?.some(f => f.name === name && f.dataSource?.query))));
    setErrors({});
    setTouched({});
    setFocusedField(null);
    setFocusedCell(null);
    setHeaderCollapsed(false);
    setLimpiezas(n => n + 1);
  };

  // Limpiar se habilita solo con captura: encabezado (campos capturables: sin hidden, disabled ni calculated) o
  // partidas (columnas del detalle) distintos del armado inicial. Vacio,
  // null y '' cuentan igual, y los numeros se comparan por valor (0 = '0'),
  // para que los ajustes automaticos de la apertura no cuenten como captura.
  const hayCaptura = () => {
    if (!schema) return false;
    const norm = (v) => {
      if (v === null || v === undefined || v === '') return '';
      const n = Number(v);
      return (typeof v !== 'boolean' && String(v).trim() !== '' && Number.isFinite(n)) ? n : String(v);
    };
    const inicialForm = armarFormDataInicial(schema);
    const capturables = schema.fields?.filter(f => f.type !== 'hidden' && !f.disabled && !f.calculated) || [];
    if (capturables.some(f => norm(formData[f.name]) !== norm(inicialForm[f.name]))) return true;
    if (!detailSchema) return false;
    const inicialDetalle = armarDetalleInicial(detailSchema);
    if (detailData.length !== inicialDetalle.length) return true;
    return detailData.some((row, i) =>
      detailSchema.columns?.some(c => norm(row[c.name]) !== norm(inicialDetalle[i][c.name])));
  };

  // ==========================================
  // RECÁLCULO EN VIVO
  // ==========================================
  useEffect(() => {
    if (!detailSchema || !parentRow) return;
    const saldoActual = parseFloat(parentRow.saldo || 0);
    const cEfectivo   = detailSchema?.c_formapago_efectivo || '01';

    const sumaImportes   = Math.round(detailData.reduce((acc, row) => acc + parseFloat(row.importe || 0), 0) * 100) / 100;
    const sumaComisiones = Math.round(detailData.reduce((acc, row) => acc + parseFloat(row.comision || 0), 0) * 100) / 100;
    const imppagoCalc    = Math.min(sumaImportes, saldoActual);

    const sumaNoEfectivo           = Math.round(detailData.filter(r => r._c_formapago !== cEfectivo).reduce((acc, r) => acc + parseFloat(r.importe || 0), 0) * 100) / 100;
    const sumaComisionesNoEfectivo = Math.round(detailData.filter(r => r._c_formapago !== cEfectivo).reduce((acc, r) => acc + parseFloat(r.comision || 0), 0) * 100) / 100;
    const saldoTrasNoEfectivo      = Math.round((saldoActual + sumaComisionesNoEfectivo - sumaNoEfectivo) * 100) / 100;
    const importeEfectivo          = Math.round(detailData.filter(r => r._c_formapago === cEfectivo).reduce((acc, r) => acc + parseFloat(r.importe || 0), 0) * 100) / 100;

    const sucambioCalc = importeEfectivo > 0
      ? Math.max(Math.round((importeEfectivo - saldoTrasNoEfectivo) * 100) / 100, 0)
      : 0;

    setFormData(prev => ({ ...prev, imppago: imppagoCalc, sucambio: sucambioCalc, impcomision: sumaComisiones }));
  }, [detailData, detailSchema, parentRow]);

  // ==========================================
  // CAMPOS CONDICIONALES (conditionalVisible / conditionalRequired)
  // ==========================================
  const isFieldVisible = (f) => {
    if (!f.conditionalVisible) return true;
    const { dependsOn, rules, default: defaultVisible = true } = f.conditionalVisible;
    const rule = rules?.find(r => String(r.value) === String(formData[dependsOn]));
    if (rule) return rule.visible !== false;
    return defaultVisible;
  };

  // Limpia a null el valor de cualquier campo con conditionalVisible en cuanto
  // deja de ser visible -- sin guarda de "touched" (a diferencia de
  // conditionalDefault en SqlexecMasterDetail.jsx): un campo oculto nunca debe
  // viajar al backend con un valor residual de cuando SI era visible. Guards
  // DENTRO del efecto -- nunca un return condicional del COMPONENTE antes del Hook.
  useEffect(() => {
    if (!schema?.fields) return;
    if (isReadOnly) return;
    const updates = {};
    schema.fields.forEach(f => {
      if (!f.conditionalVisible) return;
      if (!isFieldVisible(f) && formData[f.name] !== null && formData[f.name] !== undefined && formData[f.name] !== '') {
        updates[f.name] = null;
      }
    });
    if (Object.keys(updates).length > 0) setFormData(prev => ({ ...prev, ...updates }));
  }, [formData, schema, isReadOnly]);

  // ==========================================
  // HANDLERS ENCABEZADO
  // ==========================================
  const handleChange = (name, value, field, event) => {
    const transformar = (v) => (field?.format === 'uppercase' && typeof v === 'string') ? v.toUpperCase() : v;
    // Con el evento del campo (y su valor sin ajustar), conserva la posicion del cursor.
    const val = (event?.target && typeof value === 'string' && value === event.target.value)
      ? conservarCursor(event.target, transformar)
      : transformar(value);
    setFormData(prev => ({ ...prev, [name]: val }));
    setTouched(prev => ({ ...prev, [name]: true }));
  };

  // ==========================================
  // HANDLERS DETALLE
  // ==========================================
  const handleAddDetailRow = () => {
    if (!detailSchema) return;    
    setDetailData(prev => {
      const newRow = createEmptyDetailRow(detailSchema, prev.length + 1);
      const newData = [...prev, newRow];
      // Focus al select de la nueva fila después del render
      setTimeout(() => {
        const newIndex = newData.length - 1;
        const next = document.querySelector(`select[data-row="${newIndex}"][data-field="idsatforpago"]`);
        if (next) next.focus();
      }, 50);
      return newData;
    });
  };

  const handleDeleteDetailRow = (rowIndex) => {
    const minRows = detailSchema?.minRows || 0;
    if (detailData.length <= minRows) {
      toast.warning(`Debe haber al menos ${minRows} línea(s)`);
      return;
    }
    setDetailData(prev => {
      const next = prev.filter((_, idx) => idx !== rowIndex);
      next.forEach((row, idx) => { row.numero = idx + 1; });
      return next;
    });
  };

  const updateDetailCellValue = (rowIndex, fieldName, value) => {
    setDetailData(prev => {
      const next = [...prev];
      next[rowIndex] = { ...next[rowIndex], [fieldName]: value };
      return next;
    });
  };


  // Motor de comision/efectivo por fila -- solo aplica si el detalle
  // declara detailSchema.c_formapago_efectivo (ej. Sys_Ventas_Cobranza).
  // Sin esa clave (ej. Cxc_Cobranza_Abonos) el commit es simple: redondea
  // a 2 decimales, sin tocar columnas de comision ni saldo de parentRow.
  const commitImporteConMotor = (rowIndex, col, row, val) => {
    const numFinal = Math.max(0, Math.round(val * 100) / 100);
    if (!detailSchema?.c_formapago_efectivo) {
      const tope = parseFloat(row.saldo || 0);
      updateDetailCellValue(rowIndex, col.name, numFinal > tope ? 0 : numFinal);
      return;
    }
    const cEfectivo   = detailSchema.c_formapago_efectivo;
    const esEfectivo  = row._c_formapago === cEfectivo;
    const saldoActual = parseFloat(parentRow?.saldo || 0);
    const comisionPct = parseFloat(row._comision_pct || 0);

    const sumaOtrasNoEf   = Math.round(detailData.filter((r, i) => i !== rowIndex && r._c_formapago !== cEfectivo).reduce((acc, r) => acc + parseFloat(r.importe || 0), 0) * 100) / 100;
    const sumComOtrasNoEf = Math.round(detailData.filter((r, i) => i !== rowIndex && r._c_formapago !== cEfectivo).reduce((acc, r) => acc + parseFloat(r.comision || 0), 0) * 100) / 100;
    const saldoDisponible = Math.round((saldoActual + sumComOtrasNoEf - sumaOtrasNoEf) * 100) / 100;

    if (!esEfectivo) {
      const maxPermitido = Math.round((saldoDisponible + Math.round(saldoDisponible * comisionPct) / 100) * 100) / 100;
      if (val > maxPermitido) {
        updateDetailCellValue(rowIndex, col.name, 0);
        updateDetailCellValue(rowIndex, 'comision', 0);
        return;
      }
      const comisionMonto = numFinal >= maxPermitido
        ? Math.round(saldoDisponible * comisionPct) / 100
        : Math.round(numFinal * comisionPct) / 100;
      updateDetailCellValue(rowIndex, col.name, numFinal);
      updateDetailCellValue(rowIndex, 'comision', comisionMonto);
      return;
    }

    updateDetailCellValue(rowIndex, col.name, numFinal);
    updateDetailCellValue(rowIndex, 'comision', 0);
  };

  const pagoExactoConMotor = (rowIndex, col, row) => {
    if (!detailSchema?.c_formapago_efectivo) {
      updateDetailCellValue(rowIndex, col.name, parseFloat(row.saldo || 0));
      return;
    }
    const cEfectivo   = detailSchema.c_formapago_efectivo;
    const esEfectivo  = row._c_formapago === cEfectivo;
    const saldoActual = parseFloat(parentRow?.saldo || 0);
    const comisionPct = parseFloat(row._comision_pct || 0);

    const sumaOtrasNoEf   = Math.round(detailData.filter((r, i) => i !== rowIndex && r._c_formapago !== cEfectivo).reduce((acc, r) => acc + parseFloat(r.importe || 0), 0) * 100) / 100;
    const sumComOtrasNoEf = Math.round(detailData.filter((r, i) => i !== rowIndex && r._c_formapago !== cEfectivo).reduce((acc, r) => acc + parseFloat(r.comision || 0), 0) * 100) / 100;
    const saldoDisponible = Math.round((saldoActual + sumComOtrasNoEf - sumaOtrasNoEf) * 100) / 100;

    if (!esEfectivo) {
      const importeSugerido = Math.round((saldoDisponible + Math.round(saldoDisponible * comisionPct) / 100) * 100) / 100;
      const comisionMonto   = Math.round(saldoDisponible * comisionPct) / 100;
      updateDetailCellValue(rowIndex, col.name, importeSugerido);
      updateDetailCellValue(rowIndex, 'comision', comisionMonto);
    } else {
      updateDetailCellValue(rowIndex, col.name, saldoDisponible);
      updateDetailCellValue(rowIndex, 'comision', 0);
    }
  };
  const esColumnaEditable = (col, row) => {
    if (col.editable_when) {
      const { field, not_equals, equals } = col.editable_when;
      if (not_equals !== undefined) return row[field] !== not_equals;
      if (equals !== undefined) return row[field] === equals;
    }
    return col.editable !== false;
  };

  // Generico: oculta la celda completa (no solo disabled) cuando la condicion
  // se cumple contra la propia fila -- reemplaza el hardcode anterior de
  // 'referencia' (efectivo no tiene referencia bancaria), reutilizable para
  // cualquier columna nueva (ej. ctabancaria por partida en Contado).
  const esColumnaOculta = (col, row) => {
    if (col.hidden_when) {
      const { field, equals, not_equals } = col.hidden_when;
      if (equals !== undefined) return row[field] === equals;
      if (not_equals !== undefined) return row[field] !== not_equals;
    }
    // Fallback legacy: hasta que 'referencia' declare hidden_when en su config
    // (schema_form en BD), sigue ocultandose igual que antes.
    if (col.name === 'referencia') {
      const cEfectivo = detailSchema?.c_formapago_efectivo || '01';
      return row._c_formapago === cEfectivo;
    }
    return false;
  };

  // ==========================================
  // VALIDACIÓN
  // ==========================================
  // Logica pura (sin efectos secundarios) -- reutilizada por validateForm()
  // (submit, con setErrors) y por isFormValid (render, para el disabled del
  // boton Guardar). Evita duplicar las reglas de required/conditionalRequired.
  const computeFormErrors = ({ omitirMatchDetailSum = false } = {}) => {
    const errs = {};
    schema?.fields?.forEach(f => {
      if (!isFieldVisible(f)) return;
      let required = !!f.required;
      if (f.conditionalRequired) {
        const { dependsOn, rules } = f.conditionalRequired;
        const rule = rules?.find(r => String(r.value) === String(formData[dependsOn]));
        if (rule) required = !!rule.required;
      }
      if (required && (formData[f.name] === '' || formData[f.name] === null || formData[f.name] === undefined)) {
        errs[f.name] = `${f.label || f.name} es requerido`;
      }
    });

    // Validar min/max en campos date -- generico, soporta sentinela
    // 'TODAY'/'today' y 'PARENT:<campo>' (misma convencion que defaultValue)
    // ademas de una fecha ISO fija. Mismo nombre de atributo que ya usan
    // SqlexecMasterDetail.jsx/SqlexecBuilder.jsx (f.min/f.max).
    schema?.fields?.forEach(f => {
      if (f.type !== 'date' || !formData[f.name]) return;
      if (f.max) {
        const maxVal = resolverFechaLimite(f.max, parentRow);
        if (formData[f.name] > maxVal) {
          errs[f.name] = f.maxMessage || `${f.label || f.name} no puede ser posterior a ${maxVal}`;
        }
      }
      if (f.min) {
        const minVal = resolverFechaLimite(f.min, parentRow);
        if (formData[f.name] < minVal) {
          errs[f.name] = f.minMessage || `${f.label || f.name} no puede ser anterior a ${minVal}`;
        }
      }
    });

    // Validar ctabancaria requerido en filas no-efectivo -- config-driven:
    // solo aplica si detailSchema declara una columna 'ctabancaria' con
    // required:true (ej. Sys_Ventas_Cobranza); sin esa columna (ej.
    // Cxc_Cobranza_Abonos, que la maneja a nivel encabezado) no hace nada.
    // Usa esColumnaOculta -- misma condicion que ya oculta la celda, para
    // no exigir un valor que el usuario ni siquiera puede capturar (efectivo).
    const colCtaBancaria = detailSchema?.columns?.find(c => c.name === 'ctabancaria' && c.required);
    if (colCtaBancaria) {
      detailData.forEach((row, idx) => {
        if (esColumnaOculta(colCtaBancaria, row)) return;
        if (!row.ctabancaria) {
          errs[`_detail_ctabancaria_${idx}`] = `Cuenta Bancaria es requerida en la línea ${idx + 1}`;
        }
      });
    }

    // Validar matchDetailSum -- generico, campo del encabezado debe
    // coincidir EXACTO con la suma de un campo del detalle (ej.
    // importe_recibido vs suma de importe capturado por documento).
    // Config-driven via f.matchDetailSum -- sin esa clave, no aplica.
    schema?.fields?.forEach(f => {
      if (omitirMatchDetailSum || !f.matchDetailSum || !isFieldVisible(f)) return;
      const total = Math.round(detailData.reduce((acc, r) => acc + parseFloat(r[f.matchDetailSum.field] || 0), 0) * 100) / 100;
      const esperado = parseFloat(formData[f.name] || 0);
      if (Math.round((total - esperado) * 100) !== 0) {
        errs[f.name] = f.matchDetailSum.message || `${f.label || f.name} no coincide`;
      }
    });

    return errs;
  };

  const validateForm = () => {
    const errs = computeFormErrors();
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ==========================================
  // SUBMIT
  // ==========================================
  const handleSubmit = async () => {
    if (!validateForm()) {
      showMensajeModal('error', 'Campos incompletos', 'Completa los campos requeridos.', [], () => {});
      return;
    }
    if (detailSchema?.minRows > 0 && detailData.length === 0) {
      showMensajeModal('error', 'Falta información', 'Se requiere al menos una línea de detalle.', [], () => {});
      return;
    }

    // detailSchema.requirePositiveFields (mismo vocabulario que
    // schema.config.collapsibleHeader.requirePositiveFields): filas cuyo
    // valor no es positivo en TODOS estos campos no se envian -- ej.
    // Cxc_Cobranza_Abonos carga todas las facturas pendientes del cliente
    // en la grilla (loadDetailOnChange) pero el usuario solo abona
    // algunas; sin este filtro se creaban folios de cobro "fantasma" con
    // importe=0.00 para las facturas no tocadas (bug real confirmado
    // 17-sep, idcliecobe 66/67 en dbrbac_mt_lab).
    const requirePositiveFields = Array.isArray(detailSchema?.requirePositiveFields)
      ? detailSchema.requirePositiveFields
      : [];
    const filasParaEnviar = requirePositiveFields.length > 0
      ? detailData.filter(row => requirePositiveFields.every(f => parseFloat(row[f] || 0) > 0))
      : detailData;

    if (requirePositiveFields.length > 0 && filasParaEnviar.length === 0) {
      showMensajeModal('error', 'Falta información', `Captura un valor mayor a 0 en al menos un documento (${requirePositiveFields.join(', ')}).`, [], () => {});
      return;
    }

    // Estandar de resultados fiscales (schema_form.confirm): cerrarAlFallar
    // cierra el componente tras rechazo/error (por omision false -- los
    // formularios de captura siguen abiertos para corregir); mensajesPac,
    // errorGenerico, pasosGenericos y resultadoNoConfirmado dan texto
    // humano: nunca null/undefined ni el texto crudo del PAC como titulo.
    const confirmCfg = schema?.confirm || {};
    const alDescartarError = confirmCfg.cerrarAlFallar === true ? () => { onClose?.(); } : () => {};
    const textoLimpio = (v) => (typeof v === 'string' && v.trim() !== '' ? v.trim() : null);

    setSaving(true);
    try {
      const token = sessionStorage.getItem('mapasac_access_token');

      const masterData = {};
      schema.fields?.forEach(f => {
        if (f.name === 'idsucursal' || f.name === 'usuario') return;
        if (f.primary === true && !formData[f.name]) return;
        masterData[f.name] = formData[f.name];
      });

      const detailsClean = filasParaEnviar.map(row => {
        const clean = {};
        detailSchema.columns?.forEach(col => {
          if (!col.autoIncrement) clean[col.name] = row[col.name];
        });
        schema.config?.detail_fields?.forEach(field => {
          if (!clean.hasOwnProperty(field)) clean[field] = row[field] ?? null;
        });
        return clean;
      });

      const r = detailSchema
        ? await secureFetch(`/api/sqlexec_master_detail/${componentForm}/create`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ master: masterData, details: detailsClean })
          })
        : await secureFetch(`/api/sqlexec/${componentForm}/create`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({
              ...masterData,
              ...(accionTag ? { accion: accionTag } : {}),
              ...(origenComponentForm ? { origen_component_form: origenComponentForm } : {}),
              ...(origenKey ? { origen_key: origenKey } : {}),
              ...(autorizado ? { _autorizado: true } : {})
            })
          });

      const j = await r.json();
      if (j?.success) {
        // backend/routes/sqlexec.js envuelve la respuesta dos veces
        // ({success,message,data} dentro de {success,message,data}) -- mismo
        // unwrap que ya usa SqlexecBuilder.jsx:1330 (j.data?.data || j.data).
        const resultData = j?.data?.data || j?.data;
        const resultado = resultData?.resultado || {};

        if (resultado?.estatus_cancelacion !== undefined) {
          // Se detecta por la FORMA del dato (estatus_cancelacion = shape
          // exclusivo de factura_consultar_estatus_sat, MAPASAC-CFDI-006/007)
          // -- DEBE ir antes del branch de "uuid" de abajo: este resultado
          // tambien trae uuid (el de la factura consultada), y sin esta rama
          // caia ahi por coincidencia de forma, mostrando "Timbrado Exitoso...
          // vence el dia undefined" (vigencia_ha no existe en este shape) --
          // bug real encontrado en produccion, 2026-09-14.
          const estatusTexto = resultado.estatus_cancelacion || 'sin información (el PAC no devolvió un estatus reconocible)';
          // Titulo e icono por estado (schema_form.confirm.estatusCancelacion,
          // opcional): pendiente -> tituloPendiente/tipoPendiente, definitivo ->
          // tituloDefinitivo. Sin config queda el titulo de siempre.
          const estatusCfg = confirmCfg.estatusCancelacion || {};
          const cancelacionPendiente = !!resultado.estatus_cancelacion && !resultado.es_definitivo;
          showMensajeModal(
            (cancelacionPendiente && textoLimpio(estatusCfg.tipoPendiente)) || 'success',
            textoLimpio(cancelacionPendiente ? estatusCfg.tituloPendiente : estatusCfg.tituloDefinitivo)
              || 'Estatus Consultado',
            `Estatus real de cancelación ante el SAT: ${estatusTexto}.` +
            (resultado.estatus_cancelacion
              ? (resultado.es_definitivo ? ' La cancelación ya es definitiva.' : ' La cancelación aún no es definitiva.')
              : ''),
            [], () => { onSuccess?.(j?.data); onClose?.(); });
        } else if (resultado?.uuid) {
          // Se detecta por la FORMA del dato (trae uuid de timbrado), no por
          // nombre de componentForm -- cualquier operacion futura con este
          // mismo shape hereda el mensaje sin nuevo hardcode.
          const vigenciaFormateada = resultado.vigencia_ha
            ? resultado.vigencia_ha.split('-').reverse().join('/')
            : resultado.vigencia_ha;
          // Texto de exito configurable por formulario (schema_form.confirm.
          // exitoTitulo/exitoMensaje, placeholders {{campo}} contra el
          // resultado del timbrado + {{vigencia}} ya formateada). Sin config
          // cae al texto de siempre (Generar Factura, idsqlexec=36).
          const exitoMensaje = schema?.confirm?.exitoMensaje
            ? resolvePlaceholders(schema.confirm.exitoMensaje, { ...resultado, vigencia: vigenciaFormateada })
            : `Se ha timbrado la factura con éxito, generando CFDI: ${resultado.uuid}. ` +
              `El certificado utilizado vence el día ${vigenciaFormateada}.`;
          showMensajeModal('success', schema?.confirm?.exitoTitulo || 'Timbrado Exitoso',
            exitoMensaje,
            [], () => { onSuccess?.(j?.data); onClose?.(); });
        } else if (resultData?.idsis_cancela) {
          // Se detecta por la FORMA del dato (idsis_cancela = auditoria de
          // sis_cancela solo corre cuando factura_cancelar() confirmo exito
          // real -- doble gateo verificado: condicion del paso 'auditoria'
          // en mapasoft.sqlfunciones + el chequeo pacResultado.success en
          // backend/routes/sqlexec.js -- un rechazo del PAC nunca llega
          // hasta j.success===true con este campo poblado.
          const motivoSatLabel = selectOptions.motivo_sat
            ?.find(o => String(o.value) === String(formData.motivo_sat))?.label
            || formData.motivo_sat;
          showMensajeModal('success', 'Cancelación Exitosa',
            `Se ha cancelado el CFDI con éxito: ${formData.uuid}. ` +
            `Motivo SAT: ${motivoSatLabel}. ` +
            `Motivo interno: ${formData.motivo}.`,
            [], () => { onSuccess?.(j?.data); onClose?.(); });
        } else {
          // Texto de exito configurable por formulario (schema_form.confirm.
          // exitoTitulo/exitoMensaje, placeholders {{campo}}), misma convencion
          // que SqlexecMasterDetail. Sin config queda el texto de siempre.
          showMensajeModal('success', schema?.confirm?.exitoTitulo || 'Operación exitosa',
            schema?.confirm?.exitoMensaje
              ? resolvePlaceholders(schema.confirm.exitoMensaje, { ...formData, ...(resultData && typeof resultData === 'object' ? resultData : {}) })
              : (resultData?.message || 'Guardado correctamente'),
            [], () => { onSuccess?.(j?.data); onClose?.(); });
        }
      } else {
        if (j?.codigo_error) {
          // El PAC rechazo, pero cfdi_factura_reservar ya escribio la
          // reserva 'P' real antes de ese rechazo -- el grid debe reflejar
          // ese cambio de estado aunque la operacion general haya fallado.
          // Texto humano (schema_form.confirm): regla mensajesPac > mensaje
          // real del PAC > errorGenerico > j.message; el texto crudo del PAC
          // y el WorkProcessId van solo al detalle tecnico plegable.
          onRefreshGrid?.();
          const fuentePac = [j?.codigo_error, j?.mensaje, j?.message]
            .map(textoLimpio).filter(Boolean).join(' ').toLowerCase();
          const regla = (confirmCfg.mensajesPac || [])
            .find(r => textoLimpio(r?.contiene) && fuentePac.includes(r.contiene.toLowerCase())) || null;
          const detallePac = Object.fromEntries(Object.entries({
            'Respuesta del PAC': j?.codigo_error,
            'Mensaje del PAC': j?.mensaje,
            'WorkProcessId': j?.work_process_id
          }).filter(([, v]) => textoLimpio(v)));
          showMensajeModal('error',
            textoLimpio(regla?.titulo) || confirmCfg.errorTitulo || 'Error al guardar',
            textoLimpio(regla?.mensaje) || textoLimpio(j?.mensaje) || textoLimpio(confirmCfg.errorGenerico)
              || textoLimpio(j?.message) || 'No se pudo completar la operación.',
            Array.isArray(regla?.pasos) ? regla.pasos : (confirmCfg.pasosGenericos || []),
            alDescartarError,
            Object.keys(detallePac).length ? detallePac : null);
        } else {
          // Cubre tanto el rechazo del paso "validar" del pipeline (j.message
          // trae el motivo real de negocio) como el fallback duro de
          // sqlexec.js (respuestaError tambien llena j.message) -- un solo
          // branch generico para ambos shapes. Titulo viene de schema.confirm
          // (BD), no de un if por componentForm -- mismo patron ya usado para
          // confirm.titulo/mensaje/pregunta en estos mismos forms.
          // Excepcion: "sin saldo de timbres" es causa administrativa, no de
          // negocio -- mismo patron de deteccion por contenido que
          // MAPASAC-INV-000 (SqlexecMasterDetail.jsx), titulo especifico para
          // que el usuario no pierda tiempo revisando datos de la factura.
          // SERVICIO_NO_CONFIGURADO (409, backend/utils/servicios.js): el
          // tenant no tiene fila en mapasoft.servicios -- config administrativa
          // faltante, distinto de "sin saldo". Se detecta por code, no por texto.
          const errMsg = textoLimpio(j?.message) || textoLimpio(j?.error?.message)
            || textoLimpio(confirmCfg.errorGenerico) || 'Error al guardar';
          const errCode = j?.error?.code || j?.code;

          // Refrescar siempre, exito o no -- el grid puede haber cambiado de
          // estado en BD (ej. reserva) aunque este branch no lo detecte por
          // codigo_error especifico.
          onRefreshGrid?.();

          if (errCode === 'RECHAZO_LOCAL') {
            // Rechazo LOCAL de timbrado (antes del PAC, backend/routes/sqlexec.js):
            // el texto ya trae causa, importes, que hacer, cierre y referencia --
            // el mismo que muestran el tooltip y Ver Reserva. Sin pasos genericos
            // aparte; la informacion tecnica es la misma que la de Ver Reserva.
            showMensajeModal('error', schema?.confirm?.errorTitulo || 'Error al guardar', errMsg, [],
              alDescartarError, j?.error?.details?.tecnico || null);
          } else if (errCode === 'SERVICIO_NO_CONFIGURADO') {
            // Mismo formato estandar que cliepara/invepara. Timbrado y
            // cancelacion consumen el servicio TIMBRES -> mismo mensaje.
            showMensajeModal('error', 'Servicio de timbrado no configurado',
              'El servicio de timbrado no está configurado para tu empresa. Contacte al administrador para configurar.',
              [
                "Verifica que exista una fila en mapasoft.servicios para el tenant_id de esta empresa con clave='TIMBRES'",
                'Si no existe, un administrador debe crearla con saldo y utilizado'
              ],
              alDescartarError,
              { component_form: componentForm, tabla: 'mapasoft.servicios' });
          } else {
            const tituloError = errMsg.includes('Sin saldo de timbres disponibles')
              ? 'Sin saldo de timbres disponibles'
              : (schema?.confirm?.errorTitulo || 'Error al guardar');
            // Pasos concretos del pipeline (j.pasos) mandan sobre los genericos.
            const pasosError = Array.isArray(j?.pasos) && j.pasos.length > 0 ? j.pasos : (confirmCfg.pasosGenericos || []);
            showMensajeModal('error', tituloError, errMsg, pasosError, alDescartarError);
          }
        }
      }
    } catch (e) {
      console.error('Error en submit:', e);
      logComponentError('SqlexecMasterDetailLight', 'submit', `${componentForm} - ${e.message}`);
      onRefreshGrid?.();
      if (confirmCfg.cerrarAlFallar === true) {
        // Operacion fiscal: no sabemos si se completo del lado del servidor.
        // Se cierra y se refresca para que el usuario verifique el estado
        // real en la lista antes de reintentar (sin reintento automatico).
        showMensajeModal('warning', confirmCfg.errorTitulo || 'Error de conexión',
          textoLimpio(confirmCfg.resultadoNoConfirmado)
            || 'No pudimos confirmar el resultado. Revisa el estado del documento en la lista antes de volver a intentarlo.',
          [], alDescartarError);
      } else {
        showMensajeModal('error', 'Error de conexión',
          'No se pudo completar la operación. Verifica tu conexión e intenta de nuevo.',
          [], () => {});
      }
    } finally {
      setSaving(false);
    }
  };

  // ==========================================
  // RENDER
  // ==========================================
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <Loader2 className="animate-spin text-blue-500" size={48} />
        </div>
      </div>
    );
  }

  if (!schema) return null;

  // Declaraciones render
  const gridConfig = formulario_ui?.grid || { columns: 2, gap: '1rem' };

  const getColSpan = (gridSpan) => {
    if (!gridSpan || gridSpan === 'full') return gridConfig.columns;
    const spanMap = {
      'half': Math.floor(gridConfig.columns / 2),
      '1/2':  Math.floor(gridConfig.columns / 2),
      '1/3':  Math.floor(gridConfig.columns / 3),
      '2/3':  Math.floor(gridConfig.columns * 2 / 3),
      '1/4':  Math.floor(gridConfig.columns / 4),
      '3/4':  Math.floor(gridConfig.columns * 3 / 4)
    };
    return spanMap[gridSpan] || 1;
  };

  const visibleFields        = schema.fields?.filter(f => f.type !== 'hidden' && isFieldVisible(f)) || [];
  const visibleDetailColumns = detailSchema?.columns?.filter(c => c.visible !== false && c.type !== 'hidden') || [];
  const minVisibleRows       = (headerCollapsed && detailSchema?.minVisibleRowsCollapsed)
    ? detailSchema.minVisibleRowsCollapsed
    : (detailSchema?.minVisibleRows || 5);
  const collapsibleHeaderConfig = schema.config?.collapsibleHeader;
  const isCollapsibleHeader     = collapsibleHeaderConfig === true || collapsibleHeaderConfig?.enabled === true;
  const requirePositiveFields   = Array.isArray(collapsibleHeaderConfig?.requirePositiveFields)
    ? collapsibleHeaderConfig.requirePositiveFields
    : [];
  const rowHeightPx          = parseInt(rowsConfig.height) || 32;
  const headerHeightPx       = parseInt(headerConfig.height) || 37; // headerConfig.height es "auto" en sisconfig -- estimado por padding.y+fuente
  const alturaTablaDetalle   = headerHeightPx + rowHeightPx * minVisibleRows;
  const cEfectivoGlobal      = detailSchema?.c_formapago_efectivo || '01';
  const hayEfectivo          = detailData.some(r => r._c_formapago === cEfectivoGlobal);
  const sumaNoEfGlobal       = Math.round(detailData.filter(r => r._c_formapago !== cEfectivoGlobal).reduce((acc, r) => acc + parseFloat(r.importe || 0), 0) * 100) / 100;
  const sumComNoEfGlobal     = Math.round(detailData.filter(r => r._c_formapago !== cEfectivoGlobal).reduce((acc, r) => acc + parseFloat(r.comision || 0), 0) * 100) / 100;
  const saldoCubierto        = Math.round((parseFloat(parentRow?.saldo || 0) + sumComNoEfGlobal - sumaNoEfGlobal) * 100) / 100 <= 0;
  const hayFilaIncompleta    = detailData.some(r => !r.idsatforpago || parseFloat(r.importe || 0) === 0);

  return (
    <>
      <div
        className="fixed inset-0 flex items-center justify-center z-50 p-4"
        style={{
          backgroundColor: tabla_ui?.modals?.overlay?.backgroundColor || 'rgba(0, 0, 0, 0.6)',
          backdropFilter: tabla_ui?.modals?.overlay?.backdropFilter || 'blur(4px)'
        }}
      >
        <div
          className="w-full"
          onClick={e => e.stopPropagation()}
          style={{
            backgroundColor: modales_ui?.container?.backgroundColor || '#ffffff',
            borderRadius: modales_ui?.container?.borderRadius || '12px',
            boxShadow: modales_ui?.container?.boxShadow || '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            border: modales_ui?.container?.border || '1px solid rgba(0,0,0,0.05)',
            maxWidth: schema?.modalWidth || '50rem',
            maxHeight: compensarEscalaVh(modales_ui?.container?.maxHeight || '92vh'),
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between"
            style={{
              paddingLeft:     modales_ui?.header?.paddingX || '2rem',
              paddingRight:    modales_ui?.header?.paddingX || '2rem',
              paddingTop:      modales_ui?.header?.paddingY || '1.5rem',
              paddingBottom:   modales_ui?.header?.paddingY || '1.5rem',
              borderBottom:    modales_ui?.header?.borderBottom || '1px solid #e5e7eb',
              backgroundColor: modales_ui?.header?.backgroundColor || '#fafafa'
            }}
          >
            <div className="flex items-center" style={{ gap: modales_ui?.header?.iconGap || '1rem' }}>
              <div
                className="bg-blue-100"
                style={{
                  padding:      modales_ui?.header?.iconPadding || '0.625rem',
                  borderRadius: modales_ui?.header?.iconBorderRadius || '10px'
                }}
              >
                <Save
                  className="text-blue-600"
                  size={parseInt(modales_ui?.header?.iconSize) || 28}
                />
              </div>
              <div>
              <h2 style={{
                fontSize:      modales_ui?.header?.fontSize || '1.5rem',
                fontWeight:    modales_ui?.header?.fontWeight || '600',
                color:         modales_ui?.header?.textColor || '#111827',
                letterSpacing: modales_ui?.header?.letterSpacing || '-0.025em',
                lineHeight:    modales_ui?.header?.lineHeight || '1.2'
              }}>
                {parentRow?.saldo_timbres !== undefined && schema?.mostrar_saldo_timbres
                  ? `${schema.title || componentForm} (Timbres: ${parseInt(parentRow.saldo_timbres) === -1 ? 'sin configurar' : parentRow.saldo_timbres})`
                  : (schema.title || componentForm)}
              </h2>
                <SqlexecIdTag idsqlexec={idsqlexec} componentForm={componentForm} config={panel_ui?.identificador} />
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                padding:      modales_ui?.closeButton?.padding || '0.5rem',
                borderRadius: modales_ui?.closeButton?.borderRadius || '8px',
                color:        modales_ui?.closeButton?.color || '#6b7280',
                background: 'none', border: 'none', cursor: 'pointer'
              }}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = modales_ui?.closeButton?.hoverBackgroundColor || '#f3f4f6';
                e.currentTarget.style.color = modales_ui?.closeButton?.hoverColor || '#111827';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = modales_ui?.closeButton?.color || '#6b7280';
              }}
            >
              <X size={parseInt(modales_ui?.closeButton?.size) || 20} />
            </button>
          </div>

          {/* Content */}
          <div
            className="overflow-y-auto"
            style={{
              padding:       modales_ui?.content?.padding || '1.5rem 2rem',
              paddingTop:    modales_ui?.content?.paddingTop || '1.5rem',
              paddingBottom: modales_ui?.content?.paddingBottom || '1.5rem',
              flex: 1
            }}
          >
            {/* Context Fields */}
            {schema.contextFields && parentRow && (
              <div
                className="grid"
                style={{ gridTemplateColumns: `repeat(${gridConfig.columns}, 1fr)`, gap: gridConfig.gap, marginBottom: '1.5rem' }}
              >
                {schema.contextFields.map(cf => {
                  const esNumerico = cf.format === 'money' || /^(saldo|importe|imptot|imppago|sucambio)/i.test(cf.name);
                  const esSaldo    = cf.name === 'saldo';
                  const sumaImportes   = Math.round(detailData.reduce((acc, row) => acc + parseFloat(row.importe || 0), 0) * 100) / 100;
                  const sumaComisiones = Math.round(detailData.reduce((acc, row) => acc + parseFloat(row.comision || 0), 0) * 100) / 100;
                  const saldoEnVivo    = Math.round((parseFloat(parentRow?.saldo || 0) + sumaComisiones - sumaImportes) * 100) / 100;
                  const valorMostrado  = esNumerico
                    ? formatNumberDesktop(esSaldo ? Math.max(saldoEnVivo, 0) : (parentRow[cf.name] || 0), 2)
                    : (parentRow[cf.name] ?? '');
                  return (
                    <div key={cf.name} style={{ gridColumn: `span ${getColSpan(cf.gridSpan || 'half')}` }}>
                      <label style={{
                        fontSize:     labelStyles.fontSize || '0.875rem',
                        fontWeight:   labelStyles.fontWeight || '500',
                        color:        labelStyles.textColor || '#374151',
                        display:      'block',
                        marginBottom: labelStyles.marginBottom || '0.5rem'
                      }}>
                        {cf.label}
                      </label>
                      <input
                        type="text"
                        readOnly
                        value={valorMostrado}
                        style={{
                          width:           '100%',
                          height:          inputStyles.height || '32px',
                          fontSize:        inputStyles.fontSize || '0.75rem',
                          fontFamily:      inputStyles.fontFamily || 'inherit',
                          borderRadius:    inputStyles.borderRadius || '6px',
                          borderWidth:     inputStyles.borderWidth || '1px',
                          borderStyle:     'solid',
                          borderColor:     inputStyles.borderColor || '#d1d5db',
                          padding:         `0 ${inputStyles.padding?.x || '0.75rem'}`,
                          textAlign:       esNumerico ? 'right' : 'left',
                          backgroundColor: inputStyles.disabledBackgroundColor || '#f3f4f6',
                          color:           inputStyles.textColor || '#374151',
                          outline:         'none'
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            )}

            {/* Encabezado */}
            {visibleFields.length > 0 && (
              <>
                {isCollapsibleHeader && (() => {
                  const camposPositivosFaltantes = requirePositiveFields.filter(name => !(parseFloat(formData[name] || 0) > 0));
                  const canCollapseHeader = Object.keys(computeFormErrors({ omitirMatchDetailSum: true })).length === 0 && camposPositivosFaltantes.length === 0;
                  const bloqueado = !headerCollapsed && !canCollapseHeader;
                  const tituloBloqueo = camposPositivosFaltantes.length > 0
                    ? `Captura un valor mayor a 0 en: ${camposPositivosFaltantes.map(name => schema.fields?.find(f => f.name === name)?.label || name).join(', ')}`
                    : 'Completa los campos obligatorios para colapsar';
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <label style={{
                        fontSize:   labelStyles.fontSize || '0.875rem',
                        fontWeight: '600',
                        color:      labelStyles.textColor || '#374151'
                      }}>
                        Datos del Cobro
                      </label>
                      <button
                        type="button"
                        onClick={() => !bloqueado && setHeaderCollapsed(c => !c)}
                        disabled={bloqueado}
                        title={bloqueado ? tituloBloqueo : undefined}
                        style={{
                          background: 'none', border: 'none',
                          cursor: bloqueado ? 'not-allowed' : 'pointer',
                          opacity: bloqueado ? 0.4 : 1,
                          color: labelStyles.textColor || '#374151',
                          display: 'flex', alignItems: 'center'
                        }}
                      >
                        {headerCollapsed ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
                      </button>
                    </div>
                  );
                })()}

                {(!isCollapsibleHeader || !headerCollapsed) ? (
              <div
                className="grid"
                style={{ gridTemplateColumns: `repeat(${gridConfig.columns}, 1fr)`, gap: gridConfig.gap, marginBottom: '1.5rem' }}
              >
                {visibleFields.map(f => {
                  const showError = touched[f.name] && errors[f.name];
                  if (f.type === 'select') {
                    const isFieldDisabled = isReadOnly || (f.disabled_when && evaluarCondicion(f.disabled_when, { ...parentRow, ...formData }));
                    return (
                      <div key={f.name} style={{ gridColumn: `span ${getColSpan(f.gridSpan)}` }}>
                        <label style={{
                          fontSize:     labelStyles.fontSize || '0.875rem',
                          fontWeight:   labelStyles.fontWeight || '500',
                          color:        labelStyles.textColor || '#374151',
                          display:      'block',
                          marginBottom: labelStyles.marginBottom || '0.5rem'
                        }}>
                          {f.label}{f.required && <span style={{ color: '#ef4444' }}> *</span>}
                        </label>
                        <select
                          value={formData[f.name] || ''}
                          onChange={e => handleChange(f.name, e.target.value, f)}
                          onFocus={() => setFocusedField(f.name)}
                          onBlur={() => setFocusedField(null)}
                          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); } }}
                          disabled={isFieldDisabled}
                          style={{
                            width:           '100%',
                            height:          selectStyles.height || '32px',
                            fontSize:        selectStyles.fontSize || '0.75rem',
                            fontFamily:      selectStyles.fontFamily || 'inherit',
                            borderRadius:    selectStyles.borderRadius || '6px',
                            borderWidth:     selectStyles.borderWidth || '1px',
                            borderStyle:     'solid',
                            borderColor:     showError ? '#ef4444' : focusedField === f.name ? (selectStyles.focusBorderColor || '#3b82f6') : (selectStyles.borderColor || '#d1d5db'),
                            boxShadow:       focusedField === f.name ? `0 0 0 ${selectStyles.focusRingWidth || '2px'} ${selectStyles.focusBorderColor || '#3b82f6'}40` : 'none',
                            padding:         `0 ${selectStyles.padding?.x || '0.75rem'}`,
                            backgroundColor: isFieldDisabled ? (selectStyles.disabledBackgroundColor || '#f3f4f6') : (selectStyles.backgroundColor || '#ffffff'),
                            color:           selectStyles.textColor || '#111827',
                            outline:         'none'
                          }}
                        >
                          <option value="">Seleccionar...</option>
                          {(selectOptions[f.name] || []).map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                        {showError && (
                          <p style={{ fontSize: errorStyles.fontSize || '0.75rem', color: errorStyles.textColor || '#dc2626', marginTop: errorStyles.marginTop || '0.25rem' }}>
                            {errors[f.name]}
                          </p>
                        )}
                      </div>
                    );
                  }

                  if (f.type === 'search-modal') {
                    const displayFieldName = `${f.name}_display`;
                    const initialDisplay = formData[displayFieldName] || null;
                    return (
                      <div key={f.name} style={{ gridColumn: `span ${getColSpan(f.gridSpan)}` }}>
                        <SearchModalField
                          field={{ ...f, initialDisplayValue: initialDisplay }}
                          value={formData[f.name] || null}
                          formData={formData}
                          onChange={(value) => handleChange(f.name, value, f)}
                          onMultiFieldChange={(fields) => setFormData(prev => ({ ...prev, ...fields }))}
                          disabled={isReadOnly}
                          error={errors[f.name]}
                          touched={touched[f.name]}
                          inputStyles={inputStyles}
                          labelStyles={labelStyles}
                          errorStyles={errorStyles}
                        />
                      </div>
                    );
                  }

                  if (f.type === 'autocomplete') {
                    return (
                      <div key={f.name} style={{ gridColumn: `span ${getColSpan(f.gridSpan)}` }}>
                        <AutocompleteHeaderField
                          key={`${f.name}-${limpiezas}`}
                          field={{ ...f, initialDisplayValue: formData[`${f.name}_display`] || null }}
                          value={formData[f.name] || null}
                          onChange={(value) => handleChange(f.name, value, f)}
                          onMultiFieldChange={(fields) => setFormData(prev => ({ ...prev, ...fields }))}
                          disabled={isReadOnly}
                          error={errors[f.name]}
                          touched={touched[f.name]}
                          inputStyles={inputStyles}
                          labelStyles={labelStyles}
                          errorStyles={errorStyles}
                        />
                      </div>
                    );
                  }

                  if (f.type === 'textarea') {
                    return (
                      <div key={f.name} style={{ gridColumn: `span ${getColSpan(f.gridSpan)}` }}>
                        <label style={{
                          fontSize:     labelStyles.fontSize || '0.875rem',
                          fontWeight:   labelStyles.fontWeight || '500',
                          color:        labelStyles.textColor || '#374151',
                          display:      'block',
                          marginBottom: labelStyles.marginBottom || '0.5rem'
                        }}>
                          {f.label}{f.required && <span style={{ color: '#ef4444' }}> *</span>}
                        </label>
                        <textarea
                          rows={f.rows || 3}
                          value={formData[f.name] ?? ''}
                          onChange={e => handleChange(f.name, e.target.value, f, e)}
                          onFocus={() => setFocusedField(f.name)}
                          onBlur={() => setFocusedField(null)}
                          disabled={isReadOnly || f.disabled}
                          style={{
                            width:           '100%',
                            fontSize:        inputStyles.fontSize || '0.75rem',
                            fontFamily:      inputStyles.fontFamily || 'inherit',
                            borderRadius:    inputStyles.borderRadius || '6px',
                            borderWidth:     inputStyles.borderWidth || '1px',
                            borderStyle:     'solid',
                            borderColor:     showError ? '#ef4444' : focusedField === f.name ? (inputStyles.focusBorderColor || '#3b82f6') : (inputStyles.borderColor || '#d1d5db'),
                            boxShadow:       focusedField === f.name ? `0 0 0 ${inputStyles.focusRingWidth || '2px'} ${inputStyles.focusBorderColor || '#3b82f6'}40` : 'none',
                            padding:         `${inputStyles.padding?.y || '0.5rem'} ${inputStyles.padding?.x || '0.75rem'}`,
                            backgroundColor: (isReadOnly || f.disabled) ? (inputStyles.disabledBackgroundColor || '#f3f4f6') : (inputStyles.backgroundColor || '#ffffff'),
                            color:           inputStyles.textColor || '#111827',
                            outline:         'none',
                            resize:          'vertical'
                          }}
                        />
                        {showError && (
                          <p style={{ fontSize: errorStyles.fontSize || '0.75rem', color: errorStyles.textColor || '#dc2626', marginTop: errorStyles.marginTop || '0.25rem' }}>
                            {errors[f.name]}
                          </p>
                        )}
                      </div>
                    );
                  }

                  if (f.type === 'number' && f.inputMode === 'pos') {
                    const isFieldDisabled = isReadOnly || (f.disabled_when && evaluarCondicion(f.disabled_when, { ...parentRow, ...formData }));
                    return (
                      <div key={f.name} style={{ gridColumn: `span ${getColSpan(f.gridSpan)}` }}>
                        <label style={{
                          fontSize:     labelStyles.fontSize || '0.875rem',
                          fontWeight:   labelStyles.fontWeight || '500',
                          color:        labelStyles.textColor || '#374151',
                          display:      'block',
                          marginBottom: labelStyles.marginBottom || '0.5rem'
                        }}>
                          {f.label}{f.required && <span style={{ color: '#ef4444' }}> *</span>}
                        </label>
                        <MoneyPosHeaderInput
                          value={formData[f.name] || 0}
                          onUpdate={val => handleChange(f.name, val, f)}
                          onCommit={val => handleChange(f.name, Math.max(0, val), f)}
                          inputStyles={inputStyles}
                          focusedCell={focusedCell}
                          cellKey="importe_recibido"
                          setFocusedCell={setFocusedCell}
                          showError={showError}
                          disabled={isFieldDisabled}
                        />
                        {showError && (
                          <p style={{ fontSize: errorStyles.fontSize || '0.75rem', color: errorStyles.textColor || '#dc2626', marginTop: errorStyles.marginTop || '0.25rem' }}>
                            {errors[f.name]}
                          </p>
                        )}
                      </div>
                    );
                  }

                  return (
                    <div key={f.name} style={{ gridColumn: `span ${getColSpan(f.gridSpan)}` }}>
                      <label style={{
                        fontSize:     labelStyles.fontSize || '0.875rem',
                        fontWeight:   labelStyles.fontWeight || '500',
                        color:        labelStyles.textColor || '#374151',
                        display:      'block',
                        marginBottom: labelStyles.marginBottom || '0.5rem'
                      }}>
                        {f.label}{f.required && <span style={{ color: '#ef4444' }}> *</span>}
                      </label>
                      <input
                        type={(f.type === 'numeric' || f.type === 'number') ? 'text' : (f.type || 'text')}
                        value={(f.type === 'numeric' || f.type === 'number') ? formatNumberDesktop(formData[f.name] || 0, f.decimals ?? 2) : (formData[f.name] ?? '')}
                        max={f.type === 'date' && f.max ? resolverFechaLimite(f.max, parentRow) : undefined}
                        min={f.type === 'date' && f.min ? resolverFechaLimite(f.min, parentRow) : undefined}
                        onChange={e => {
                          let val = e.target.value;
                          if (f.type === 'date' && val) {
                            if (f.max) {
                              const maxVal = resolverFechaLimite(f.max, parentRow);
                              if (val > maxVal) val = maxVal;
                            }
                            if (f.min) {
                              const minVal = resolverFechaLimite(f.min, parentRow);
                              if (val < minVal) val = minVal;
                            }
                          }
                          handleChange(f.name, val, f, e);
                        }}
                        onFocus={() => setFocusedField(f.name)}
                        onBlur={() => setFocusedField(null)}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); } }}
                        disabled={isReadOnly || f.disabled || (f.disabled_when && evaluarCondicion(f.disabled_when, { ...parentRow, ...formData }))}
                        style={{
                          width:           '100%',
                          height:          inputStyles.height || '32px',
                          fontSize:        inputStyles.fontSize || '0.75rem',
                          fontFamily:      inputStyles.fontFamily || 'inherit',
                          borderRadius:    inputStyles.borderRadius || '6px',
                          borderWidth:     inputStyles.borderWidth || '1px',
                          borderStyle:     'solid',
                          borderColor:     showError ? '#ef4444' : focusedField === f.name ? (inputStyles.focusBorderColor || '#3b82f6') : (inputStyles.borderColor || '#d1d5db'),
                          boxShadow:       focusedField === f.name ? `0 0 0 ${inputStyles.focusRingWidth || '2px'} ${inputStyles.focusBorderColor || '#3b82f6'}40` : 'none',
                          padding:         `0 ${inputStyles.padding?.x || '0.75rem'}`,
                          textAlign:       f.align || ((f.type === 'numeric' || f.type === 'number') ? 'right' : 'left'),
                          backgroundColor: (isReadOnly || f.disabled || (f.disabled_when && evaluarCondicion(f.disabled_when, { ...parentRow, ...formData }))) ? (inputStyles.disabledBackgroundColor || '#f3f4f6') : (inputStyles.backgroundColor || '#ffffff'),
                          color:           inputStyles.textColor || '#111827',
                          outline:         'none'
                        }}
                      />
                      {showError && (
                        <p style={{ fontSize: errorStyles.fontSize || '0.75rem', color: errorStyles.textColor || '#dc2626', marginTop: errorStyles.marginTop || '0.25rem' }}>
                          {errors[f.name]}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
                ) : (
                  <div style={{
                    marginBottom:    '1.5rem',
                    padding:         '0.75rem 1rem',
                    textAlign:       'center',
                    backgroundColor: tabla_ui?.hint?.backgroundColor || '#eff6ff',
                    borderRadius:    tabla_ui?.hint?.borderRadius || '6px',
                    border:          `1px solid ${tabla_ui?.hint?.borderColor || '#bfdbfe'}`,
                    fontSize:        tabla_ui?.hint?.fontSize || '0.75rem',
                    color:           tabla_ui?.hint?.color || '#1d4ed8',
                    fontWeight:      tabla_ui?.hint?.fontWeight || '500'
                  }}>
                    Cliente: {formData.idclientes_display || '—'}   |   Importe Recibido: {formatNumberDesktop(formData.importe_recibido || 0, 2)}
                  </div>
                )}
              </>
            )}

            {/* Detalle */}
            {detailSchema && (
              <div>
                {detailSchema.label && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                    <label style={{
                      fontSize:   labelStyles.fontSize || '0.875rem',
                      fontWeight: '600',
                      color:      labelStyles.textColor || '#374151'
                    }}>
                      {detailSchema.label}
                    </label>
                    {schema.hint_orden && (
                      <span style={{
                        fontSize:  tabla_ui?.hint?.fontSize || '0.75rem',
                        color:     tabla_ui?.hint?.infoColor || '#92400e',
                        fontWeight: '400'
                      }}>
                        {schema.hint_orden}
                      </span>
                    )}
                  </div>
                )}

                <div style={{ height: `${alturaTablaDetalle}px`, overflowY: 'auto', overflowX: 'hidden', border: tabla_ui?.table?.border || '1px solid #d1d5db' }}>
                  <table className="w-full text-sm" style={{ borderCollapse: tabla_ui?.table?.borderCollapse || 'collapse', width: '100%' }}>
                    <thead>
                      <tr>
                        {visibleDetailColumns.map(col => (
                          <th
                            key={col.name}
                            className="whitespace-nowrap"
                            style={{
                              width:           col.width,
                              textAlign:       col.align || headerConfig.textAlign || 'left',
                              paddingLeft:     headerConfig.padding?.x || '0.5rem',
                              paddingRight:    headerConfig.padding?.x || '0.5rem',
                              paddingTop:      headerConfig.padding?.y || '0.5rem',
                              paddingBottom:   headerConfig.padding?.y || '0.5rem',
                              fontSize:        headerConfig.fontSize || '0.75rem',
                              fontFamily:      headerConfig.fontFamily || 'inherit',
                              fontWeight:      headerConfig.fontWeight || '600',
                              textTransform:   headerConfig.textTransform || 'uppercase',
                              color:           headerConfig.color || '#374151',
                              height:          headerConfig.height || 'auto',
                              backgroundColor: headerConfig.backgroundColor || '#f3f4f6',
                              borderBottom:    headerConfig.borderBottom
                                ? `${headerConfig.borderBottom.width} ${headerConfig.borderBottom.style} ${headerConfig.borderBottom.color}`
                                : '2px solid #e5e7eb',
                              borderRight:     headerConfig.borderRight || '1px solid #d1d5db',
                              borderTop:       headerConfig.borderTop || '1px solid #d1d5db',
                              position:        'sticky',
                              top:             0,
                              zIndex:          1
                            }}
                          >
                            {col.label}
                          </th>
                        ))}
                        {!isReadOnly && detailSchema?.allowDelete !== false && (
                          <th
                            className="whitespace-nowrap"
                            style={{
                              width:           '50px',
                              backgroundColor: headerConfig.backgroundColor || '#f3f4f6',
                              borderBottom:    headerConfig.borderBottom
                                ? `${headerConfig.borderBottom.width} ${headerConfig.borderBottom.style} ${headerConfig.borderBottom.color}`
                                : '2px solid #e5e7eb',
                              borderRight:     headerConfig.borderRight || '1px solid #d1d5db',
                              borderTop:       headerConfig.borderTop || '1px solid #d1d5db'
                            }}
                          />
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {detailData.length === 0 ? (
                        <tr>
                          <td
                            colSpan={visibleDetailColumns.length + (!isReadOnly && detailSchema?.allowDelete !== false ? 1 : 0)}
                            style={{
                              textAlign:    'center',
                              padding:      '2rem',
                              borderBottom: rowsConfig.borderBottom || '1px solid #e5e7eb'
                            }}
                          >
                            <div className="flex flex-col items-center justify-center gap-3">
                              <div
                                className="rounded-full flex items-center justify-center"
                                style={{ width: '64px', height: '64px', backgroundColor: '#f3f4f6' }}
                              >
                                <Receipt size={32} className="text-gray-400" />
                              </div>
                              <p className="font-medium" style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                Sin partidas de documentos pendientes por cobrar
                              </p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        detailData.map((row, rowIndex) => {
                          const rowBg = rowIndex % 2 === 0 ? (rowsConfig.odd || '#ffffff') : (rowsConfig.even || '#f9fafb');
                          return (
                            <tr
                              key={row._id}
                              style={{ backgroundColor: rowBg }}
                              onMouseEnter={e => e.currentTarget.style.backgroundColor = rowsConfig.hoverColor || '#f0f7ff'}
                              onMouseLeave={e => e.currentTarget.style.backgroundColor = rowBg}
                            >
                              {visibleDetailColumns.map(col => {
                                const editable = !isReadOnly && esColumnaEditable(col, row);

                                if (esColumnaOculta(col, row)) {
                                  return (
                                    <td key={col.name} style={{
                                      height:       rowsConfig.height || '32px',
                                      paddingLeft:  '0.5rem', paddingRight: '0.5rem',
                                      paddingTop:   '0.125rem', paddingBottom: '0.125rem',
                                      borderRight:  rowsConfig.borderRight || '1px solid #e5e7eb',
                                      borderBottom: rowsConfig.borderBottom || '1px solid #e5e7eb'
                                    }}>
                                      <span style={{ fontSize: rowsConfig.fontSize || '0.75rem', color: '#d1d5db' }}>—</span>
                                    </td>
                                  );
                                }

                                if (col.autoIncrement) {
                                  return (
                                    <td key={col.name} style={{
                                      height:       rowsConfig.height || '32px',
                                      textAlign:    'center',
                                      paddingLeft:  '0.5rem', paddingRight: '0.5rem',
                                      paddingTop:   '0.125rem', paddingBottom: '0.125rem',
                                      fontSize:     rowsConfig.fontSize || '0.75rem',
                                      fontFamily:   rowsConfig.fontFamily || 'inherit',
                                      color:        rowsConfig.textColor || '#374151',
                                      borderRight:  rowsConfig.borderRight || '1px solid #e5e7eb',
                                      borderBottom: rowsConfig.borderBottom || '1px solid #e5e7eb'
                                    }}>
                                      {row.numero}
                                    </td>
                                  );
                                }

                                // SELECT
                                if (col.type === 'select') {
                                  const opciones = col.optionsField ? (row[col.optionsField] || []) : (detailSelectOptions[col.name] || []);
                                  return (
                                    <td key={col.name} style={{
                                      height:       rowsConfig.height || '32px',
                                      paddingLeft:  '0.5rem', paddingRight: '0.5rem',
                                      paddingTop:   '0.125rem', paddingBottom: '0.125rem',
                                      fontSize:     rowsConfig.fontSize || '0.75rem',
                                      fontFamily:   rowsConfig.fontFamily || 'inherit',
                                      color:        rowsConfig.textColor || '#374151',
                                      borderRight:  rowsConfig.borderRight || '1px solid #e5e7eb',
                                      borderBottom: rowsConfig.borderBottom || '1px solid #e5e7eb'
                                    }}>
                                      {editable ? (
                                        <select
                                          data-row={rowIndex}
                                          data-field={col.name}
                                          value={row[col.name] ?? ''}
                                          onChange={e => {
                                            const value = e.target.value;
                                            if (col.optionsField) {
                                              // Select generico por fila (opciones ya embebidas en la fila) --
                                              // solo asigna el valor, sin la logica de forma de pago/comision.
                                              updateDetailCellValue(rowIndex, col.name, value);
                                              return;
                                            }
                                            if (col.name !== 'idsatforpago') {
                                              // Select generico por fila CON dataSource propio pero que no es
                                              // la forma de pago (ej. ctabancaria en Sys_Ventas_Cobranza) --
                                              // solo asigna el valor. La logica de abajo (_c_formapago,
                                              // _comision_pct, reset de importe/comision/referencia) es
                                              // exclusiva de idsatforpago -- aplicarla aqui borraria datos
                                              // ya capturados en la fila al solo elegir la cuenta bancaria.
                                              updateDetailCellValue(rowIndex, col.name, value);
                                              return;
                                            }
                                            const opcionSeleccionada = opciones.find(o => String(o[col.dataSource?.valueField || 'value']) === String(value));
                                            const cFormapagoSeleccionado = opcionSeleccionada?.c_formapago;
                                            const cEfectivo = detailSchema?.c_formapago_efectivo || '01';
                                            const esEfectivo = cFormapagoSeleccionado === cEfectivo;
                                            if (esEfectivo) {
                                              const yaHayEfectivo = detailData.some((r, idx) => idx !== rowIndex && r._c_formapago === cEfectivo);
                                              if (yaHayEfectivo) { handleDeleteDetailRow(rowIndex); return; }
                                            }
                                            updateDetailCellValue(rowIndex, col.name, value);
                                            updateDetailCellValue(rowIndex, '_c_formapago', cFormapagoSeleccionado);
                                            updateDetailCellValue(rowIndex, '_comision_pct', opcionSeleccionada?.comision || 0);
                                            updateDetailCellValue(rowIndex, 'importe', 0);
                                            updateDetailCellValue(rowIndex, 'comision', 0);
                                            updateDetailCellValue(rowIndex, 'referencia', '');
                                          }}
                                          onFocus={() => setFocusedCell(`${col.name}-${rowIndex}`)}
                                          onBlur={() => setFocusedCell(null)}
                                          onKeyDown={e => {
                                            if (e.key === 'Enter') {
                                              e.preventDefault();
                                              const navConfig = detailSchema?.navigation?.[col.name];
                                              const destino = resolverDestinoEnter(navConfig?.onEnter, row);
                                              if (destino) {
                                                // Sin restringir a 'input' -- el destino puede ser un
                                                // <select> (ej. ctabancaria), no solo un <input> (ej. importe).
                                                const next = document.querySelector(`[data-row="${rowIndex}"][data-field="${destino}"]`);
                                                if (next) next.focus();
                                              }
                                            }
                                          }}
                                          style={{
                                            width:           '100%',
                                            height:          selectStyles.height || '28px',
                                            fontSize:        selectStyles.fontSize || '0.75rem',
                                            fontFamily:      selectStyles.fontFamily || 'inherit',
                                            borderRadius:    selectStyles.borderRadius || '4px',
                                            borderWidth:     selectStyles.borderWidth || '1px',
                                            borderStyle:     'solid',
                                            borderColor:     focusedCell === `${col.name}-${rowIndex}`
                                              ? (selectStyles.focusBorderColor || '#3b82f6')
                                              : 'transparent',
                                            boxShadow:       focusedCell === `${col.name}-${rowIndex}`
                                              ? `0 0 0 ${selectStyles.focusRingWidth || '2px'} ${selectStyles.focusBorderColor || '#3b82f6'}40`
                                              : 'none',
                                            padding:         `0 ${selectStyles.padding?.x || '0.5rem'}`,
                                            backgroundColor: focusedCell === `${col.name}-${rowIndex}`
                                              ? (selectStyles.backgroundColor || '#ffffff')
                                              : 'transparent',
                                            color:           selectStyles.textColor || '#111827',
                                            appearance:      focusedCell === `${col.name}-${rowIndex}` ? 'auto' : 'none',
                                            outline:         'none',
                                            cursor:          'pointer'
                                          }}
                                        >
                                          <option value="">Seleccionar...</option>
                                          {opciones.map(opt => (
                                            <option key={opt[col.dataSource?.valueField || 'value']} value={opt[col.dataSource?.valueField || 'value']}>
                                              {opt[col.dataSource?.labelField || 'label']}
                                            </option>
                                          ))}
                                        </select>
                                      ) : (
                                        <span style={{ fontSize: rowsConfig.fontSize || '0.75rem', color: '#9ca3af' }}>
                                          {opciones.find(o => String(o[col.dataSource?.valueField || 'value']) === String(row[col.name]))?.[col.dataSource?.labelField || 'label'] || '—'}
                                        </span>
                                      )}
                                    </td>
                                  );
                                }

                                // NUMBER
                                if (col.type === 'number') {
                                  return (
                                    <td key={col.name} style={{
                                      height:       rowsConfig.height || '32px',
                                      paddingLeft:  '0.5rem', paddingRight: '0.5rem',
                                      paddingTop:   '0.125rem', paddingBottom: '0.125rem',
                                      fontSize:     rowsConfig.fontSize || '0.75rem',
                                      fontFamily:   rowsConfig.fontFamily || 'inherit',
                                      color:        rowsConfig.textColor || '#374151',
                                      borderRight:  rowsConfig.borderRight || '1px solid #e5e7eb',
                                      borderBottom: rowsConfig.borderBottom || '1px solid #e5e7eb'
                                    }}>
                                      {editable ? (
                                        col.inputMode === 'pos' ? (
                                          <MoneyPosInputCell
                                            value={row[col.name]}
                                            onUpdate={val => updateDetailCellValue(rowIndex, col.name, val)}
                                            onCommit={val => commitImporteConMotor(rowIndex, col, row, val)}
                                            inputStyles={inputStyles}
                                            focusedCell={focusedCell}
                                            cellKey={`${col.name}-${rowIndex}`}
                                            setFocusedCell={setFocusedCell}
                                            dataRow={rowIndex}
                                            dataField={col.name}
                                            onEnter={() => {
                                              const navConfig = detailSchema?.navigation?.[col.name];
                                              if (navConfig?.onEnter === 'nextRowSameField') {
                                                const nextRowIndex = (rowIndex + 1) % detailData.length;
                                                const next = document.querySelector(`input[data-row="${nextRowIndex}"][data-field="${col.name}"]`);
                                                if (next) { next.focus(); return; }
                                              }
                                              if (navConfig?.onEnter) {
                                                const next = document.querySelector(`input[data-row="${rowIndex}"][data-field="${navConfig.onEnter}"]`);
                                                if (next) { next.focus(); return; }
                                                document.activeElement?.blur();
                                              }
                                            }}
                                            onPagoExacto={() => pagoExactoConMotor(rowIndex, col, row)}
                                          />
                                        ) : (
                                          <BankInputCell
                                            value={row[col.name]}
                                            onUpdate={val => updateDetailCellValue(rowIndex, col.name, val)}
                                            onCommit={val => commitImporteConMotor(rowIndex, col, row, val)}
                                            inputStyles={inputStyles}
                                            rowsConfig={rowsConfig}
                                            focusedCell={focusedCell}
                                            cellKey={`${col.name}-${rowIndex}`}
                                            setFocusedCell={setFocusedCell}
                                            dataRow={rowIndex}
                                            dataField={col.name}
                                            onEnter={() => {
                                              const navConfig = detailSchema?.navigation?.[col.name];
                                              if (navConfig?.onEnter === 'nextRowSameField') {
                                                const nextRowIndex = (rowIndex + 1) % detailData.length;
                                                const next = document.querySelector(`input[data-row="${nextRowIndex}"][data-field="${col.name}"]`);
                                                if (next) { next.focus(); return; }
                                              }
                                              if (navConfig?.onEnter) {
                                                const next = document.querySelector(`input[data-row="${rowIndex}"][data-field="${navConfig.onEnter}"]`);
                                                if (next) { next.focus(); return; }
                                                // Efectivo: no hay referencia → blur limpio
                                                document.activeElement?.blur();
                                              }
                                            }}
                                            onPagoExacto={() => pagoExactoConMotor(rowIndex, col, row)}
                                          />
                                        )
                                      ) : (
                                        <span style={{ fontSize: rowsConfig.fontSize || '0.75rem', display: 'block', textAlign: 'right' }}>
                                          {formatNumberDesktop(row[col.name] || 0, col.decimals ?? 2)}
                                        </span>
                                      )}
                                    </td>
                                  );
                                }

                                // TEXT
                                return (
                                  <td key={col.name} style={{
                                    height:       rowsConfig.height || '32px',
                                    paddingLeft:  '0.5rem', paddingRight: '0.5rem',
                                    paddingTop:   '0.125rem', paddingBottom: '0.125rem',
                                    fontSize:     rowsConfig.fontSize || '0.75rem',
                                    fontFamily:   rowsConfig.fontFamily || 'inherit',
                                    color:        rowsConfig.textColor || '#374151',
                                    borderRight:  rowsConfig.borderRight || '1px solid #e5e7eb',
                                    borderBottom: rowsConfig.borderBottom || '1px solid #e5e7eb'
                                  }}>
                                    {editable ? (
                                      <input
                                        type="text"
                                        data-row={rowIndex}
                                        data-field={col.name}
                                        value={row[col.name] ?? ''}
                                        onFocus={() => setFocusedCell(`${col.name}-${rowIndex}`)}
                                        onBlur={() => setFocusedCell(null)}
                                        onChange={e => {
                                          const v = conservarCursor(e.target, (texto) => texto.toUpperCase().replace(/[^A-Z0-9\-\/]/g, ''));
                                          updateDetailCellValue(rowIndex, col.name, v);
                                        }}
                                        onKeyDown={e => {
                                          if (e.key === 'Enter') {
                                            e.preventDefault();
                                            const navConfig = detailSchema?.navigation?.[col.name];
                                            if (navConfig?.onEnter === 'nextRow') {
                                              const next = document.querySelector(`select[data-row="${rowIndex + 1}"][data-field="idsatforpago"]`);
                                              if (next) { next.focus(); return; }
                                              const btnAgregar = document.querySelector('button[data-agregar]');
                                              if (btnAgregar) { btnAgregar.focus(); return; }
                                                e.target.blur();
                                            }
                                          }
                                        }}
                                        style={{
                                          width:           '100%',
                                          fontSize:        inputStyles.fontSize || '0.75rem',
                                          fontFamily:      inputStyles.fontFamily || 'inherit',
                                          borderRadius:    inputStyles.borderRadius || '4px',
                                          borderWidth:     inputStyles.borderWidth || '1px',
                                          borderStyle:     'solid',
                                          borderColor:     focusedCell === `${col.name}-${rowIndex}`
                                            ? (inputStyles.focusBorderColor || '#3b82f6')
                                            : 'transparent',
                                          padding:         '0.25rem',
                                          backgroundColor: focusedCell === `${col.name}-${rowIndex}`
                                            ? (inputStyles.backgroundColor || '#ffffff')
                                            : 'transparent',
                                          outline:         'none'
                                        }}
                                      />
                                    ) : (
                                      <span style={{ fontSize: rowsConfig.fontSize || '0.75rem', color: rowsConfig.textColor || '#374151' }}>
                                        {row[col.name] || '—'}
                                      </span>
                                    )}
                                  </td>
                                );
                              })}

                              {/* Eliminar */}
                              {!isReadOnly && detailSchema?.allowDelete !== false && (
                                <td style={{
                                  textAlign:    'center',
                                  paddingLeft:  '0.25rem', paddingRight: '0.25rem',
                                  paddingTop:   '0.125rem', paddingBottom: '0.125rem',
                                  fontSize:     rowsConfig.fontSize || '0.75rem',
                                  borderRight:  rowsConfig.borderRight || '1px solid #e5e7eb',
                                  borderBottom: rowsConfig.borderBottom || '1px solid #e5e7eb'
                                }}>
                                  <button
                                    onClick={() => detailData.length > (detailSchema?.minRows || 0) ? handleDeleteDetailRow(rowIndex) : null}
                                    disabled={detailData.length <= (detailSchema?.minRows || 0)}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      cursor: detailData.length <= (detailSchema?.minRows || 0) ? 'not-allowed' : 'pointer',
                                      color: detailData.length <= (detailSchema?.minRows || 0) ? '#d1d5db' : '#dc2626'
                                    }}
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </td>
                              )}
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Hint */}
                {(() => {
                  const cEfectivo = detailSchema?.c_formapago_efectivo || '01';
                  const sumaNoEfectivo           = Math.round(detailData.filter(r => r._c_formapago !== cEfectivo).reduce((acc, r) => acc + parseFloat(r.importe || 0), 0) * 100) / 100;
                  const sumaComisionesNoEfectivo = Math.round(detailData.filter(r => r._c_formapago !== cEfectivo).reduce((acc, r) => acc + parseFloat(r.comision || 0), 0) * 100) / 100;
                  const importeEfectivo          = Math.round(detailData.filter(r => r._c_formapago === cEfectivo).reduce((acc, r) => acc + parseFloat(r.importe || 0), 0) * 100) / 100;
                  const saldoBase = Math.round((parseFloat(parentRow?.saldo || 0) + sumaComisionesNoEfectivo - sumaNoEfectivo - importeEfectivo) * 100) / 100;
                  const cobroCompleto = saldoBase <= 0;

                  const lineaActiva        = detailData.find(row => parseFloat(row.importe || 0) === 0 && row.idsatforpago);
                  const comisionPct        = parseFloat(lineaActiva?._comision_pct || 0);
                  const comisionProyectada = Math.round(saldoBase * comisionPct) / 100;
                  const importeSugerido    = Math.round((saldoBase + comisionProyectada) * 100) / 100;

                  return !cobroCompleto && (
                    <div style={{
                      marginTop:       '0.75rem',
                      padding:         '0.75rem 1rem',
                      backgroundColor: tabla_ui?.hint?.backgroundColor || '#eff6ff',
                      borderRadius:    tabla_ui?.hint?.borderRadius || '6px',
                      border:          `1px solid ${tabla_ui?.hint?.borderColor || '#bfdbfe'}`,
                      fontSize:        tabla_ui?.hint?.fontSize || '0.75rem',
                      color:           tabla_ui?.hint?.color || '#1d4ed8',
                      fontWeight:      tabla_ui?.hint?.fontWeight || '500'
                    }}>
                      💡 Para cerrar el cobro capture: {formatNumberDesktop(importeSugerido, 2)}   |   [PgDn] Pago Exacto
                    </div>
                  );
                })()}

                {/* Comparación en tiempo real -- campos con matchDetailSum */}
                {schema.fields?.filter(f => f.matchDetailSum && isFieldVisible(f)).map(f => {
                  const total = Math.round(detailData.reduce((acc, r) => acc + parseFloat(r[f.matchDetailSum.field] || 0), 0) * 100) / 100;
                  const esperado = parseFloat(formData[f.name] || 0);
                  const diferencia = Math.round((esperado - total) * 100) / 100;
                  const coincide = diferencia === 0;
                  return (
                    <div key={f.name} data-comparacion-hint={f.name} style={{
                      marginTop:       '0.75rem',
                      padding:         '0.75rem 1rem',
                      textAlign:       'center',
                      backgroundColor: coincide ? (tabla_ui?.hint?.successBackgroundColor || '#f0fdf4') : (tabla_ui?.hint?.warningBackgroundColor || '#fffbeb'),
                      borderRadius:    tabla_ui?.hint?.borderRadius || '6px',
                      border:          `1px solid ${coincide ? (tabla_ui?.hint?.successBorderColor || '#bbf7d0') : (tabla_ui?.hint?.warningBorderColor || '#fde68a')}`,
                      fontSize:        tabla_ui?.hint?.fontSize || '0.75rem',
                      color:           coincide ? (tabla_ui?.hint?.successColor || '#15803d') : (tabla_ui?.hint?.warningColor || '#92400e'),
                      fontWeight:      tabla_ui?.hint?.fontWeight || '500'
                    }}>
                      Capturado: {formatNumberDesktop(total, 2)}   |   {f.label}: {formatNumberDesktop(esperado, 2)}   |   Diferencia: {formatNumberDesktop(Math.abs(diferencia), 2)}
                    </div>
                  );
                })}

                {/* + Agregar */}
                {detailSchema.allowAdd !== false && !isReadOnly && !hayEfectivo && !saldoCubierto && !hayFilaIncompleta && (
                  <button
                    data-agregar
                    onClick={handleAddDetailRow}
                    style={{
                      marginTop:  '0.75rem',
                      display:    'flex', alignItems: 'center', gap: '0.5rem',
                      fontSize:   btnBase.fontSize || '0.75rem',
                      fontWeight: btnBase.fontWeight || '500',
                      color:      btnPrimary.backgroundColor || '#3b82f6',
                      background: 'none', border: 'none', cursor: 'pointer'
                    }}
                  >
                    <Plus size={16} /> Agregar
                  </button>
                )}

              </div>
            )}
          </div>

          {/* Footer */}
          <div
            className="flex justify-end"
            style={{
              paddingLeft:     modales_ui?.footer?.paddingX || '2rem',
              paddingRight:    modales_ui?.footer?.paddingX || '2rem',
              paddingTop:      modales_ui?.footer?.paddingY || '1.25rem',
              paddingBottom:   modales_ui?.footer?.paddingY || '1.25rem',
              borderTop:       modales_ui?.footer?.borderTop || '1px solid #e5e7eb',
              backgroundColor: modales_ui?.footer?.backgroundColor || 'transparent',
              gap:             '0.75rem'
            }}
          >
            {!isReadOnly && schema?.config?.botonLimpiar?.enabled && (() => {
              const limpiarDisabled = saving || !hayCaptura();
              return (
                <button
                  data-limpiar
                  onClick={limpiarFormulario}
                  disabled={limpiarDisabled}
                  style={{
                    height:          btnBase.height || '32px',
                    fontSize:        btnBase.fontSize || '0.75rem',
                    fontFamily:      btnBase.fontFamily || 'inherit',
                    fontWeight:      btnBase.fontWeight || '500',
                    paddingLeft:     btnBase.padding?.x || '1rem',
                    paddingRight:    btnBase.padding?.x || '1rem',
                    borderRadius:    btnBase.borderRadius || '6px',
                    borderWidth:     btnBase.borderWidth || '1px',
                    borderStyle:     'solid',
                    borderColor:     limpiarDisabled
                      ? (btnSecondary.disabledBorderColor || btnSecondary.borderColor || '#d1d5db')
                      : (btnSecondary.borderColor || '#d1d5db'),
                    backgroundColor: limpiarDisabled
                      ? (btnSecondary.disabledBackgroundColor || btnBase.disabledBackgroundColor || '#f3f4f6')
                      : (btnSecondary.backgroundColor || '#ffffff'),
                    color:           limpiarDisabled
                      ? (btnSecondary.disabledTextColor || '#9ca3af')
                      : (btnSecondary.textColor || '#374151'),
                    cursor:          limpiarDisabled ? 'not-allowed' : 'pointer'
                  }}
                  onMouseEnter={e => {
                    if (!limpiarDisabled) {
                      e.currentTarget.style.backgroundColor = btnSecondary.hoverBackgroundColor || '#f9fafb';
                      e.currentTarget.style.borderColor = btnSecondary.hoverBorderColor || '#9ca3af';
                    }
                  }}
                  onMouseLeave={e => {
                    if (!limpiarDisabled) {
                      e.currentTarget.style.backgroundColor = btnSecondary.backgroundColor || '#ffffff';
                      e.currentTarget.style.borderColor = btnSecondary.borderColor || '#d1d5db';
                    }
                  }}
                >
                  {schema.config.botonLimpiar.label || 'Limpiar'}
                </button>
              );
            })()}

            <button
              onClick={onClose}
              disabled={saving}
              style={{
                height:          btnBase.height || '32px',
                fontSize:        btnBase.fontSize || '0.75rem',
                fontFamily:      btnBase.fontFamily || 'inherit',
                fontWeight:      btnBase.fontWeight || '500',
                paddingLeft:     btnBase.padding?.x || '1rem',
                paddingRight:    btnBase.padding?.x || '1rem',
                borderRadius:    btnBase.borderRadius || '6px',
                borderWidth:     btnBase.borderWidth || '1px',
                borderStyle:     'solid',
                borderColor:     btnSecondary.borderColor || '#d1d5db',
                backgroundColor: btnSecondary.backgroundColor || '#ffffff',
                color:           btnSecondary.textColor || '#374151',
                cursor:          saving ? 'not-allowed' : 'pointer'
              }}
              onMouseEnter={e => {
                if (!saving) {
                  e.currentTarget.style.backgroundColor = btnSecondary.hoverBackgroundColor || '#f9fafb';
                  e.currentTarget.style.borderColor = btnSecondary.hoverBorderColor || '#9ca3af';
                }
              }}
              onMouseLeave={e => {
                if (!saving) {
                  e.currentTarget.style.backgroundColor = btnSecondary.backgroundColor || '#ffffff';
                  e.currentTarget.style.borderColor = btnSecondary.borderColor || '#d1d5db';
                }
              }}
            >
              Cancelar
            </button>

            {!isReadOnly && (() => {
              const saldoPendienteRender = Math.round((
                parseFloat(parentRow?.saldo || 0) +
                Math.round(detailData.filter(r => r._c_formapago !== cEfectivoGlobal).reduce((acc, r) => acc + parseFloat(r.comision || 0), 0) * 100) / 100 -
                Math.round(detailData.reduce((acc, r) => acc + parseFloat(r.importe || 0), 0) * 100) / 100
              ) * 100) / 100;
              // -1 = servicio no configurado ; 0 = sin saldo. Ambos bloquean Guardar.
              const stRawBtn = schema?.mostrar_saldo_timbres ? parentRow?.saldo_timbres : undefined;
              const stBtn = (stRawBtn === undefined || stRawBtn === null) ? null : parseInt(stRawBtn);
              const sinTimbres = stBtn === -1 || stBtn === 0;
              const isFormValid = Object.keys(computeFormErrors()).length === 0;
              const btnDisabled = saving || (detailSchema ? saldoPendienteRender > 0 : false) || sinTimbres || !isFormValid;
              return (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.25rem' }}>
                  <button
                    data-guardar
                    onClick={() => schema?.confirm ? setShowConfirma(true) : handleSubmit()}
                    disabled={btnDisabled}
                    style={{
                      height:          btnBase.height || '32px',
                      fontSize:        btnBase.fontSize || '0.75rem',
                      fontFamily:      btnBase.fontFamily || 'inherit',
                      fontWeight:      btnBase.fontWeight || '500',
                      paddingLeft:     btnBase.padding?.x || '1rem',
                      paddingRight:    btnBase.padding?.x || '1rem',
                      borderRadius:    btnBase.borderRadius || '6px',
                      border:          'none',
                      backgroundColor: btnDisabled
                        ? (btnBase.disabledBackgroundColor || '#9ca3af')
                        : (botones_ui?.success?.backgroundColor || '#16a34a'),
                      color:           btnDisabled
                        ? (btnBase.disabledTextColor || '#ffffff')
                        : (botones_ui?.success?.textColor || '#ffffff'),
                      cursor:          btnDisabled ? 'not-allowed' : 'pointer',
                      display:         'flex', alignItems: 'center', gap: '0.5rem'
                    }}
                    onMouseEnter={e => {
                      if (!btnDisabled) e.currentTarget.style.backgroundColor = botones_ui?.success?.hoverBackgroundColor || '#15803d';
                    }}
                    onMouseLeave={e => {
                      if (!btnDisabled) e.currentTarget.style.backgroundColor = botones_ui?.success?.backgroundColor || '#16a34a';
                    }}
                  >
                    {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
                    {saving ? 'Guardando...' : 'Guardar'}
                  </button>
                  {sinTimbres && (
                    <span style={{ fontSize: errorStyles.fontSize || '0.75rem', color: errorStyles.textColor || '#dc2626' }}>
                      {stBtn === -1 ? 'Servicio de timbrado no configurado' : 'Sin saldo de timbres disponibles'}
                    </span>
                  )}
                </div>
              );
            })()}
          </div>

        </div>
      </div>

      {showConfirma && schema?.confirm && schema.confirm.tipo && schema.confirm.pregunta && (
        <SqlexecConfirmDialog
          isOpen={true}
          tipo={schema.confirm.tipo}
          pregunta={schema.confirm.pregunta}
          onClose={() => setShowConfirma(false)}
          onConfirm={() => { setShowConfirma(false); handleSubmit(); }}
        />
      )}

      {showConfirma && schema?.confirm && !(schema.confirm.tipo && schema.confirm.pregunta) && (
        <SqlexecMasterDetail
          key="confirm-light"
          componentForm="Sys_Confirma_Mensaje"
          mode="create"
          useGenerico={true}
          initialData={{
            idconfirma: 1,
            mode:     schema.confirm.mode || 'create',
            titulo:   (schema.confirm.titulo || '').replace('{{noremision}}', parentRow?.noremision || ''),
            partidas: detailData.length,
            total:    new Intl.NumberFormat('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(formData[schema.confirm.total_field] || 0)
          }}
          onClose={() => setShowConfirma(false)}
          onSuccess={() => { setShowConfirma(false); handleSubmit(); }}
        />
      )}

      <SqlexecMensaje
        show={showMensaje}
        type={mensajeData.type}
        title={mensajeData.title}
        message={mensajeData.message}
        steps={mensajeData.steps}
        technicalInfo={mensajeData.technicalInfo}
        actions={[{
          label: 'Entendido',
          variant: 'primary',
          onClick: () => {
            setShowMensaje(false);
            // onDismiss explicito (Sys_Ventas_GenerarFactura: refrescar grid
            // + cerrar TODO via onSuccess, no via onClose) manda sobre la
            // regla generica. 'info' solo cierra el aviso -- el modal padre
            // sigue abierto para reintentar. Cualquier otro caso sin
            // onDismiss: onClose cierra todo, como antes.
            if (mensajeData.onDismiss) mensajeData.onDismiss();
            else if (mensajeData.type !== 'info') onClose?.();
          }
        }]}
        onClose={() => {
          setShowMensaje(false);
          if (mensajeData.onDismiss) mensajeData.onDismiss();
          else if (mensajeData.type !== 'info') onClose?.();
        }}
      />
    </>
  );
};

export default SqlexecMasterDetailLight;